import {
    Box,
    ButtonGroup,
    FormControl,
    FormGroup,
    FormLabel,
    Grid,
    IconButton,
    InputLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React, {
    ReactElement,
    useContext,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import EscalatedModal, { inferEscalatedModal } from './EscalatedModal';
import {
    AppFormikDatePicker,
    AppFormikSelect,
    AppFormikTextArea,
    AppFormikTextField,
    AppPrimaryButton,
    AppRadioBox,
} from 'shared/components/AppComponents';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FormikSolicitorDocumentProps,
    Steps,
    defaultReviewedState,
    DocumentMeta,
} from '../SolicitorDocumentPage';
import { ClientService, ManagePacketService } from 'services';
import { FieldArray, useFormikContext } from 'formik';
import { Utils } from 'shared/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { LOCATION_CHANGE } from 'redux-first-history';
import { Action } from 'history';
import CloseIcon from '@mui/icons-material/Close';
import { BrowserMeta } from 'BrowserHistory';

const Footer: React.FC<FooterProps> = React.forwardRef(
    (props, ref): ReactElement => {
        const role = useSelector((state: any) => state.authState.role);
        const DocumentMetaProps = React.useContext(DocumentMeta);
        const dispatch = useDispatch();
        const location = useLocation();
        const browserProps = useContext(BrowserMeta);
        const router = useSelector((state: any) => state.router);
        const currentIdx = useMemo(
            () =>
                window.history.state &&
                typeof window.history.state.idx !== 'undefined'
                    ? window.history.state.idx
                    : null,
            []
        );

        const [isCachedRestore, setCachedRestored] = useState<boolean>(false);
        const recordLocations = useSelector(
            (state: any) => state.persistState.recordLocations
        );

        const isLocationPrimaryRoute = useMemo(
            () =>
                router.previousLocations.some(
                    (e: any, index: number) =>
                        e.location.pathname === '/manage-packets' && index === 1
                ),
            [location.key, recordLocations]
        );

        const cachedBackState = useMemo(() => browserProps?.backState, []);
        const documentReloaded = useMemo(
            () => browserProps?.isDocumentReload,
            []
        );
        const isRestorePreviousState = useMemo(
            () =>
                ((router && router.action !== 'REPLACE') || isCachedRestore) &&
                cachedBackState !== null,
            [isCachedRestore, router]
        );
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const isRestoreLastState = useMemo(
            () =>
                ((router && router.action !== 'REPLACE') || isCachedRestore) &&
                isLocationPrimaryRoute === false &&
                documentReloaded,
            [isCachedRestore, documentReloaded, isLocationPrimaryRoute, router]
        );
        const broadcastReviewedArray = useSelector(
            (state: any) => state.broadcastState.broadcastReviewedData
        );

        const defaultPacketState: defaultReviewedState = useMemo(() => {
            if (isRestorePreviousState || isRestoreLastState) {
                const packetActionsDocumentdataUUID =
                    location.state &&
                    location.state.documentData &&
                    location.state.documentData.actions &&
                    location.state.documentData.actions.uuid
                        ? location.state.documentData.actions.uuid
                        : undefined;
                const packetActionsUUID =
                    location.state &&
                    location.state.actions &&
                    location.state.actions.uuid
                        ? location.state.actions.uuid
                        : packetActionsDocumentdataUUID;
                const reviewData =
                    location.state && location.state.reviewData
                        ? location.state.reviewData
                        : undefined;
                const prevLocation = broadcastReviewedArray.find(
                    (e: any) =>
                        e.pathname === '/single-reviewed' &&
                        e &&
                        e.state !== null &&
                        e.state.documentData &&
                        e.state.documentData.actions &&
                        e.state.documentData.actions.uuid &&
                        e.state.documentData.actions.uuid === packetActionsUUID
                );

                const prevLocationState =
                    prevLocation && prevLocation.state
                        ? prevLocation.state
                        : undefined;

                const prevState =
                    isRestorePreviousState && cachedBackState
                        ? cachedBackState
                        : prevLocationState;
                const prevDocumentData =
                    prevState && prevState.documentData
                        ? prevState.documentData
                        : undefined;
                return {
                    ...location.state,
                    ...prevDocumentData,
                    reviewData:
                        prevState && prevState.reviewData
                            ? prevState.reviewData
                            : reviewData,
                };
            }
            if (
                location.state === null &&
                router.location &&
                typeof router.location.state === 'object' &&
                router.location.pathname === location.pathname
            ) {
                return router.location.state;
            }
            return location.state;
        }, [isRestoreLastState, isRestorePreviousState]);

        useLayoutEffect(() => {
            if (isRestorePreviousState || isRestoreLastState) {
                setCachedRestored(true);
            }
        }, [isRestorePreviousState, isRestoreLastState]);
        const currentLength = useMemo(
            () =>
                window.history.length &&
                typeof window.history.length !== 'undefined'
                    ? window.history.length
                    : null,
            []
        );

        const isHistoricRouter = useMemo(
            () =>
                router &&
                (router.action === 'REPLACE' || router.action === 'PUSH') &&
                typeof currentLength === 'number' &&
                (currentLength === 2 || currentLength === 1) &&
                typeof currentIdx === 'number' &&
                currentIdx === 0,
            [currentIdx, currentLength, router]
        );

        const {
            onStepChanged,
            onValidationCategoryChanged,
            onStepClicked,
            onChangedDropdown,
        } = props;
        const {
            values,
            getFieldHelpers,
            validateForm,
            setFieldTouched,
            // setFieldValue,
            initialValues,
        } = useFormikContext<FormikSolicitorDocumentProps>();
        const [isActiveBackNavigation, setActiveBackNavigation] =
            React.useState<boolean>(false);
        const [data, setData] = useState<ReviewData[]>([]);

        const [clientId, setClientId] = useState<string>('');
        const [packetId, setPacketId] = useState<string>('');
        const [Solicitors, SetSolicitors] = React.useState<string[]>([]);
        const [SolicitorsData, SetSolicitorsData] = React.useState<
            SolicitorData[]
        >([]);
        const [, setStep] = useState<number>(0);
        const [currentSequence, setSequence] = useState<number>(0);
        const [currentSequenceData, setSequenceData] = useState<ReviewData>();

        const navigation = useNavigate();
        // const router = useSelector((state: any) => state.router);
        const [isChekedFirst, setCheckedFirstDocument] =
            React.useState<boolean>(false);
        const [isChekedSecond, setCheckedSecondDocument] =
            React.useState<boolean>(false);
        const [openDetail, setOpenDetail] = React.useState<
            | 'Correct'
            | 'N/A'
            | 'Send For Rework'
            | 'Document Missing'
            | 'Escalated'
            | 'Paper Document'
            | 'Hand Signed'
            | 'Sign Not Verified'
            | 'ViewAlfy'
        >();

        const maxSequence = React.useMemo(() => {
            if (typeof data !== 'undefined') {
                const dataMaxStep = data
                    .sort((a, b) => b.sequence - a.sequence)
                    .at(0);
                if (dataMaxStep) {
                    return dataMaxStep.sequence;
                }
            }
            return 0;
        }, [data]);

        const dropDownSequenceMenu = React.useMemo(() => {
            if (typeof data !== 'undefined') {
                const ListOfStep = data
                    .filter(
                        (e) =>
                            e.isRead === true || e.sequence === currentSequence
                    )
                    .sort((a, b) => b.sequence - a.sequence);
                if (ListOfStep) {
                    return Array.from(ListOfStep).map(
                        (e) => `Step ${e.sequence}`
                    );
                }
            }
            return [];
        }, [currentSequence, data]);
        const escalatedModalRef = React.useRef<inferEscalatedModal | null>(
            null
        );

        const isSendToReworkFlag = React.useMemo(
            () => data.some((i) => i.result === 'Send For Rework'),
            [data]
        );

        const isEscalatedFlag = React.useMemo(
            () => data.some((i) => i.result === 'Escalated'),
            [data]
        );

        const isEscalated = React.useMemo(
            () =>
                defaultPacketState &&
                defaultPacketState.actions &&
                defaultPacketState.actions.type
                    ? defaultPacketState.actions.type === 'Escalated'
                    : false,
            []
        );
        const isReworkPosibleFlag = React.useMemo(
            () => currentSequenceData?.reworkPossible,
            [currentSequenceData]
        );

        useLayoutEffect(() => {
            setData(
                Array.from(data).map((element) => {
                    if (element.sequence === currentSequence) {
                        return {
                            ...element,
                            isRead: true,
                        };
                    }
                    return element;
                })
            );
        }, [currentSequence]);
        const isMissingDocument = React.useMemo(() => {
            if (currentSequenceData?.result === 'Document Missing') {
                return true;
            }
            if (
                currentSequenceData &&
                currentSequenceData.result !== 'Paper Document'
            ) {
                if (
                    currentSequenceData.foundDocuments === 2 &&
                    currentSequenceData.requiredDocuments === 2 &&
                    ((typeof currentSequenceData.compareTo === 'object' &&
                        (currentSequenceData.compareTo.documentId === null ||
                            typeof currentSequenceData.compareTo.documentId ===
                                'undefined')) ||
                        (typeof currentSequenceData.compareWith === 'object' &&
                            (currentSequenceData.compareWith.documentId ===
                                null ||
                                typeof currentSequenceData.compareWith
                                    .documentId === 'undefined')))
                ) {
                    return true;
                }
                if (
                    currentSequenceData.foundDocuments === 2 &&
                    currentSequenceData.requiredDocuments === 1 &&
                    typeof currentSequenceData.compareTo === 'object' &&
                    (currentSequenceData.compareTo.documentId === null ||
                        typeof currentSequenceData.compareTo.documentId ===
                            'undefined') &&
                    typeof currentSequenceData.compareWith === 'object' &&
                    (currentSequenceData.compareWith.documentId === null ||
                        typeof currentSequenceData.compareWith.documentId ===
                            'undefined')
                ) {
                    return true;
                }
                if (
                    currentSequenceData.foundDocuments === 1 &&
                    currentSequenceData.requiredDocuments === 1 &&
                    typeof currentSequenceData.compareTo === 'object' &&
                    (currentSequenceData.compareTo.documentId === null ||
                        typeof currentSequenceData.compareTo.documentId ===
                            'undefined') &&
                    typeof currentSequenceData.compareWith === 'object' &&
                    (currentSequenceData.compareWith.documentId === null ||
                        typeof currentSequenceData.compareWith.documentId ===
                            'undefined')
                ) {
                    return true;
                }
                if (
                    currentSequenceData.foundDocuments === 1 &&
                    currentSequenceData.requiredDocuments === 2 &&
                    ((typeof currentSequenceData.compareTo === 'object' &&
                        (currentSequenceData.compareTo.documentId === null ||
                            typeof currentSequenceData.compareTo.documentId ===
                                'undefined')) ||
                        (typeof currentSequenceData.compareWith === 'object' &&
                            (currentSequenceData.compareWith.documentId ===
                                null ||
                                typeof currentSequenceData.compareWith
                                    .documentId === 'undefined')))
                ) {
                    return true;
                }
                if (
                    currentSequenceData.foundDocuments === 0 &&
                    currentSequenceData.requiredDocuments === 1 &&
                    typeof currentSequenceData.compareTo === 'object' &&
                    (currentSequenceData.compareTo.documentId === null ||
                        typeof currentSequenceData.compareTo.documentId ===
                            'undefined') &&
                    typeof currentSequenceData.compareWith === 'object' &&
                    (currentSequenceData.compareWith.documentId === null ||
                        typeof currentSequenceData.compareWith.documentId ===
                            'undefined')
                ) {
                    return true;
                }
            }
            return false;
        }, [currentSequenceData]);

        const getDrawnStatus = React.useCallback(
            (drawn: boolean | undefined, active: boolean | undefined) => {
                if (typeof drawn === 'boolean') {
                    return drawn;
                }
                return typeof active === 'boolean' && active === true
                    ? active
                    : false;
            },
            []
        );
        const isPaperDocument = React.useMemo(
            () =>
                data.some(
                    (i) =>
                        i.result === 'Paper Document' &&
                        i.actions.uuid === currentSequenceData?.actions.uuid
                ),
            [currentSequenceData?.actions.uuid, data]
        );

        React.useLayoutEffect(() => {
            if (
                typeof onStepChanged === 'function' &&
                currentSequenceData &&
                data.length > 0
            )
                onStepChanged(currentSequence, currentSequenceData, data);
        }, [currentSequence, onStepChanged]);

        React.useLayoutEffect(() => {
            if (typeof onValidationCategoryChanged === 'function' && openDetail)
                onValidationCategoryChanged(openDetail);
        }, [openDetail]);

        const isRightJSONPath = React.useMemo(
            () =>
                currentSequenceData &&
                currentSequenceData.compareTo &&
                currentSequenceData.compareTo.paths &&
                currentSequenceData.compareTo.paths.length > 0,
            [currentSequenceData]
        );
        const isLeftJSONPath = React.useMemo(
            () =>
                currentSequenceData &&
                currentSequenceData.compareWith &&
                currentSequenceData.compareWith.paths &&
                currentSequenceData.compareWith.paths.length > 0,
            [currentSequenceData]
        );

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const isRightJSONUri = React.useMemo(
            () =>
                currentSequenceData &&
                currentSequenceData.compareTo &&
                currentSequenceData.compareTo.file
                    ? currentSequenceData.compareTo.file?.includes('.json')
                    : false,
            [currentSequenceData]
        );

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const isLeftJSONUri = React.useMemo(
            () =>
                currentSequenceData &&
                currentSequenceData.compareWith &&
                currentSequenceData.compareWith.file
                    ? currentSequenceData.compareWith.file?.includes('.json')
                    : false,
            [currentSequenceData]
        );

        React.useLayoutEffect(() => {
            const currentIndex = data.findIndex(
                (o) => o.sequence === currentSequence
            );

            if (typeof currentIndex === 'number' && currentIndex >= 0) {
                const isEscalated = getFieldHelpers(
                    `reviewedSteps[${currentIndex}].isEscalated`
                );
                const isRework = getFieldHelpers(
                    `reviewedSteps[${currentIndex}].isRework`
                );

                if (
                    currentSequence > 0 &&
                    currentSequenceData?.result === 'Send For Rework'
                ) {
                    const solicitorField = getFieldHelpers(
                        `reviewedSteps[${currentIndex}].solicitor`
                    );

                    const datePickerField = getFieldHelpers(
                        `reviewedSteps[${currentIndex}].datepickerRework`
                    );

                    const firstDocBool = getFieldHelpers(
                        `reviewedSteps[${currentIndex}].isFirstDocument`
                    );
                    const secondDocBool = getFieldHelpers(
                        `reviewedSteps[${currentIndex}].isSecondDocument`
                    );

                    isEscalated.setValue(false);
                    isRework.setValue(true);
                    ClientService.getSolicitors({
                        clientId,
                    }).then((response: SolicitorsListResponse) => {
                        SetSolicitorsData(response.data.data);
                        SetSolicitors(
                            Array.from(response.data.data).map(
                                (element) =>
                                    `${element.name} - ${element.email.id}`
                            )
                        );

                        const updatedData = data.find(
                            (o) =>
                                o.actions.uuid ===
                                    currentSequenceData.actions.uuid &&
                                typeof o.solicitorData !== 'undefined'
                        );
                        if (
                            updatedData &&
                            typeof updatedData.solicitorData !== 'undefined'
                        ) {
                            solicitorField.setValue(
                                `${updatedData.solicitorData.name} - ${updatedData.solicitorData.email.id}`
                            );
                        }
                    });

                    if (currentSequenceData.firstReason) {
                        firstDocBool.setValue(true);
                        setCheckedFirstDocument(true);
                    } else {
                        firstDocBool.setValue(false);
                        setCheckedFirstDocument(false);
                    }
                    if (currentSequenceData.secondReason) {
                        secondDocBool.setValue(true);
                        setCheckedSecondDocument(true);
                    } else {
                        // secondReasonField.setValue('');
                        secondDocBool.setValue(false);
                        setCheckedSecondDocument(false);
                    }
                    if (
                        data.some(
                            (o, i) =>
                                i === currentSequence - 1 && o.solicitorData
                        )
                    ) {
                        const populatedStepData = data.find(
                            (o, i) =>
                                i === currentSequence - 1 && o.solicitorData
                        );

                        if (populatedStepData) {
                            solicitorField.setValue(
                                populatedStepData.solicitorData?.name,
                                true
                            );

                            datePickerField.setValue(
                                populatedStepData.expiryDate,
                                true
                            );
                        }
                    }
                } else if (
                    currentSequence > 0 &&
                    currentSequenceData?.result === 'Escalated'
                ) {
                    // need to be fixed
                    isRework.setValue(false);
                    isEscalated.setValue(true);
                } else {
                    isRework.setValue(false);
                    isEscalated.setValue(false);
                }
                if (!openDetail) {
                    setOpenDetail(currentSequenceData?.result);
                }
            }
        }, [openDetail, currentSequence]);

        React.useLayoutEffect(() => {
            const currentIndex = data.findIndex(
                (o) => o.sequence === currentSequence
            );

            if (typeof currentIndex === 'number' && currentIndex >= 0) {
                const isDisplayedEscalatedModal = getFieldHelpers(
                    `reviewedSteps[${currentIndex}].isDisplayedEscalatedModal`
                );
                const currentReviewedStep = values.reviewedSteps[currentIndex];

                if (currentReviewedStep) {
                    const isEscalatedModal =
                        currentReviewedStep.isDisplayedEscalatedModal;
                    if (isEscalatedModal) {
                        escalatedModalRef.current?.setOpen();
                        escalatedModalRef.current?.setReason(
                            currentReviewedStep.escalatedReason
                        );
                        isDisplayedEscalatedModal.setValue(false);
                    }
                }
            }
        }, [currentSequence, initialValues]);

        /**
         * action when step processed to the next stepData
         */
        const actionOnProcessedToStepNext = React.useCallback(async () => {
            const nextStepSequenceFromData = Array.from(data)
                .sort((a, b) => a.sequence - b.sequence)
                .find(
                    (el) =>
                        el.sequence >= currentSequence + 1 &&
                        // eslint-disable-next-line no-underscore-dangle
                        el.stack.knownInstance()
                );
            const nextStep = nextStepSequenceFromData
                ? nextStepSequenceFromData.sequence
                : 0;
            const currentIndex = data.findIndex(
                (o) => o.sequence === currentSequence
            );
            if (typeof onStepClicked === 'function') {
                onStepClicked();
            }
            const validate = await validateForm()
                .then((response) => {
                    let validated = true;

                    if (
                        response.reviewedSteps &&
                        typeof currentIndex === 'number' &&
                        currentIndex >= 0
                    ) {
                        const currentSequenceResponse =
                            response.reviewedSteps[currentIndex];

                        if (
                            response &&
                            currentSequenceResponse &&
                            openDetail === 'Send For Rework' &&
                            (Object.keys(currentSequenceResponse).includes(
                                'solicitor'
                            ) ||
                                Object.keys(currentSequenceResponse).includes(
                                    'datepickerRework'
                                ) ||
                                Object.keys(currentSequenceResponse).includes(
                                    'firstReason'
                                ) ||
                                Object.keys(currentSequenceResponse).includes(
                                    'secondReason'
                                ) ||
                                Object.keys(currentSequenceResponse).includes(
                                    'firstReasonPreDefined'
                                ) ||
                                Object.keys(currentSequenceResponse).includes(
                                    'secondReasonPreDefined'
                                ))
                        ) {
                            setFieldTouched(
                                `reviewedSteps[${currentIndex}].solicitor`,
                                true
                            );
                            setFieldTouched(
                                `reviewedSteps[${currentIndex}].datepickerRework`,
                                true
                            );
                            setFieldTouched(
                                `reviewedSteps[${currentIndex}].firstReason`,
                                true
                            );
                            setFieldTouched(
                                `reviewedSteps[${currentIndex}].secondReason`,
                                true
                            );
                            setFieldTouched(
                                `reviewedSteps[${currentIndex}].firstReasonPreDefined`,
                                true
                            );
                            setFieldTouched(
                                `reviewedSteps[${currentIndex}].secondReasonPreDefined`,
                                true
                            );
                            validated = false;
                        } else if (
                            response &&
                            currentSequenceResponse &&
                            Object.keys(currentSequenceResponse).includes(
                                'escalatedReason'
                            )
                        ) {
                            setFieldTouched(
                                `reviewedSteps[${currentIndex}].escalatedReason`,
                                true
                            );
                            validated = false;
                        }
                    }

                    return validated;
                })
                .catch(() => false);

            if (
                validate &&
                typeof currentIndex === 'number' &&
                currentIndex >= 0
            ) {
                if (data.some((o) => o.sequence === nextStep)) {
                    const stepData = data.find((o) => o.sequence === nextStep);

                    const currentreviewedStepData =
                        values.reviewedSteps[currentIndex];

                    if (openDetail === 'Send For Rework') {
                        const solicitor = SolicitorsData.find(
                            (o) =>
                                `${o.name} - ${o.email.id}` ===
                                currentreviewedStepData.solicitor
                        );
                        // lock solicitor to use one person
                        if (solicitor) {
                            setData(
                                Array.from(
                                    Array.from(data).map((element) => {
                                        if (
                                            element.sequence === currentSequence
                                        ) {
                                            return {
                                                ...element,
                                                compareTo: {
                                                    ...element.compareTo,
                                                    coverLetterDetails:
                                                        element.compareTo &&
                                                        Object.keys(
                                                            element.compareTo
                                                        ).length > 0 &&
                                                        element.compareTo
                                                            .coverLetterDetails !==
                                                            null
                                                            ? {
                                                                  ...element
                                                                      .compareTo
                                                                      .coverLetterDetails,
                                                                  sortedVertices:
                                                                      Array.from(
                                                                          element
                                                                              .compareTo
                                                                              .coverLetterDetails
                                                                              .sortedVertices ??
                                                                              []
                                                                      ).map(
                                                                          (
                                                                              o
                                                                          ) => ({
                                                                              ...o,
                                                                              isActive:
                                                                                  false,
                                                                              isDrawn:
                                                                                  getDrawnStatus(
                                                                                      o.isDrawn,
                                                                                      o.isActive
                                                                                  ),
                                                                          })
                                                                      ),
                                                              }
                                                            : null,
                                                    sortedVertices: Array.from(
                                                        element.compareTo
                                                            .sortedVertices ??
                                                            []
                                                    ).map((o) => ({
                                                        ...o,
                                                        isActive: false,
                                                        isDrawn: getDrawnStatus(
                                                            o.isDrawn,
                                                            o.isActive
                                                        ),
                                                    })),
                                                },
                                                compareWith: {
                                                    ...element.compareWith,
                                                    coverLetterDetails:
                                                        element.compareWith &&
                                                        Object.keys(
                                                            element.compareWith
                                                        ).length > 0 &&
                                                        element.compareWith
                                                            .coverLetterDetails !==
                                                            null
                                                            ? {
                                                                  ...element
                                                                      .compareWith
                                                                      .coverLetterDetails,
                                                                  sortedVertices:
                                                                      Array.from(
                                                                          element
                                                                              .compareWith
                                                                              .coverLetterDetails
                                                                              .sortedVertices ??
                                                                              []
                                                                      ).map(
                                                                          (
                                                                              o
                                                                          ) => ({
                                                                              ...o,
                                                                              isActive:
                                                                                  false,
                                                                              isDrawn:
                                                                                  getDrawnStatus(
                                                                                      o.isDrawn,
                                                                                      o.isActive
                                                                                  ),
                                                                          })
                                                                      ),
                                                              }
                                                            : null,
                                                    sortedVertices: Array.from(
                                                        element.compareWith
                                                            .sortedVertices ??
                                                            []
                                                    ).map((o) => ({
                                                        ...o,
                                                        isActive: false,
                                                        isDrawn: getDrawnStatus(
                                                            o.isDrawn,
                                                            o.isActive
                                                        ),
                                                    })),
                                                },
                                                firstReason:
                                                    currentreviewedStepData.firstReason,
                                                secondReason:
                                                    currentreviewedStepData.secondReason,
                                                firstReasonPreDefined:
                                                    currentreviewedStepData.firstReasonPreDefined,
                                                secondReasonPreDefined:
                                                    currentreviewedStepData.secondReasonPreDefined,

                                                isFirstDocument:
                                                    currentreviewedStepData.isFirstDocument,
                                                isSecondDocument:
                                                    currentreviewedStepData.isSecondDocument,
                                                // expiryDate: values.datepickerRework,
                                            };
                                        }
                                        return element;
                                    })
                                ).map((el) => ({
                                    ...el,
                                    solicitorData: solicitor,
                                    expiryDate:
                                        currentreviewedStepData.datepickerRework,
                                }))
                            );
                            // fieldStepHelper.setValue(
                            //     Array.from(values.reviewedSteps).map(
                            //         (element) => ({
                            //             ...element,
                            //             solicitorData: solicitor,
                            //             expiryDate:
                            //                 currentreviewedStepData.datepickerRework,
                            //         })
                            //     )
                            // );
                        } else {
                            setData(
                                Array.from(data).map((element) => {
                                    if (element.sequence === currentSequence) {
                                        return {
                                            ...element,
                                            compareTo: {
                                                ...element.compareTo,
                                                coverLetterDetails:
                                                    element.compareTo &&
                                                    Object.keys(
                                                        element.compareTo
                                                    ).length > 0 &&
                                                    element.compareTo
                                                        .coverLetterDetails !==
                                                        null
                                                        ? {
                                                              ...element
                                                                  .compareTo
                                                                  .coverLetterDetails,
                                                              sortedVertices:
                                                                  Array.from(
                                                                      element
                                                                          .compareTo
                                                                          .coverLetterDetails
                                                                          .sortedVertices ??
                                                                          []
                                                                  ).map(
                                                                      (o) => ({
                                                                          ...o,
                                                                          isActive:
                                                                              false,
                                                                          isDrawn:
                                                                              getDrawnStatus(
                                                                                  o.isDrawn,
                                                                                  o.isActive
                                                                              ),
                                                                      })
                                                                  ),
                                                          }
                                                        : null,
                                                sortedVertices: Array.from(
                                                    element.compareTo
                                                        .sortedVertices ?? []
                                                ).map((o) => ({
                                                    ...o,
                                                    isActive: false,
                                                    isDrawn: getDrawnStatus(
                                                        o.isDrawn,
                                                        o.isActive
                                                    ),
                                                })),
                                            },
                                            compareWith: {
                                                ...element.compareWith,
                                                coverLetterDetails:
                                                    element.compareWith &&
                                                    Object.keys(
                                                        element.compareWith
                                                    ).length > 0 &&
                                                    element.compareWith
                                                        .coverLetterDetails !==
                                                        null
                                                        ? {
                                                              ...element
                                                                  .compareWith
                                                                  .coverLetterDetails,
                                                              sortedVertices:
                                                                  Array.from(
                                                                      element
                                                                          .compareWith
                                                                          .coverLetterDetails
                                                                          .sortedVertices ??
                                                                          []
                                                                  ).map(
                                                                      (o) => ({
                                                                          ...o,
                                                                          isActive:
                                                                              false,
                                                                          isDrawn:
                                                                              getDrawnStatus(
                                                                                  o.isDrawn,
                                                                                  o.isActive
                                                                              ),
                                                                      })
                                                                  ),
                                                          }
                                                        : null,
                                                sortedVertices: Array.from(
                                                    element.compareWith
                                                        .sortedVertices ?? []
                                                ).map((o) => ({
                                                    ...o,
                                                    isActive: false,
                                                    isDrawn: getDrawnStatus(
                                                        o.isDrawn,
                                                        o.isActive
                                                    ),
                                                })),
                                            },
                                            firstReason:
                                                currentreviewedStepData.firstReason,
                                            secondReason:
                                                currentreviewedStepData.secondReason,
                                            firstReasonPreDefined:
                                                currentreviewedStepData.firstReasonPreDefined,
                                            secondReasonPreDefined:
                                                currentreviewedStepData.secondReasonPreDefined,

                                            isFirstDocument:
                                                currentreviewedStepData.isFirstDocument,
                                            isSecondDocument:
                                                currentreviewedStepData.isSecondDocument,
                                            // expiryDate: values.datepickerRework,
                                        };
                                    }
                                    return element;
                                })
                            );
                        }
                    } else if (openDetail === 'Escalated') {
                        setData(
                            Array.from(data).map((element) => {
                                if (element.sequence === currentSequence) {
                                    return {
                                        ...element,
                                        compareTo: {
                                            ...element.compareTo,
                                            coverLetterDetails:
                                                element.compareTo &&
                                                Object.keys(element.compareTo)
                                                    .length > 0 &&
                                                element.compareTo
                                                    .coverLetterDetails !== null
                                                    ? {
                                                          ...element.compareTo
                                                              .coverLetterDetails,
                                                          sortedVertices:
                                                              Array.from(
                                                                  element
                                                                      .compareTo
                                                                      .coverLetterDetails
                                                                      .sortedVertices ??
                                                                      []
                                                              ).map((o) => ({
                                                                  ...o,
                                                                  isActive:
                                                                      false,
                                                                  isDrawn:
                                                                      getDrawnStatus(
                                                                          o.isDrawn,
                                                                          o.isActive
                                                                      ),
                                                              })),
                                                      }
                                                    : null,
                                            sortedVertices: Array.from(
                                                element.compareTo
                                                    .sortedVertices ?? []
                                            ).map((o) => ({
                                                ...o,
                                                isActive: false,
                                                isDrawn: getDrawnStatus(
                                                    o.isDrawn,
                                                    o.isActive
                                                ),
                                            })),
                                        },
                                        compareWith: {
                                            ...element.compareWith,
                                            coverLetterDetails:
                                                element.compareWith &&
                                                Object.keys(element.compareWith)
                                                    .length > 0 &&
                                                element.compareWith
                                                    .coverLetterDetails !== null
                                                    ? {
                                                          ...element.compareWith
                                                              .coverLetterDetails,
                                                          sortedVertices:
                                                              Array.from(
                                                                  element
                                                                      .compareWith
                                                                      .coverLetterDetails
                                                                      .sortedVertices ??
                                                                      []
                                                              ).map((o) => ({
                                                                  ...o,
                                                                  isActive:
                                                                      false,
                                                                  isDrawn:
                                                                      getDrawnStatus(
                                                                          o.isDrawn,
                                                                          o.isActive
                                                                      ),
                                                              })),
                                                      }
                                                    : null,
                                            sortedVertices: Array.from(
                                                element.compareWith
                                                    .sortedVertices ?? []
                                            ).map((o) => ({
                                                ...o,
                                                isActive: false,
                                                isDrawn: getDrawnStatus(
                                                    o.isDrawn,
                                                    o.isActive
                                                ),
                                            })),
                                        },
                                        escalatedReason:
                                            currentreviewedStepData.escalatedReason,
                                    };
                                }
                                return element;
                            })
                        );
                    } else {
                        setData(
                            Array.from(data).map((element) => {
                                if (element.sequence === currentSequence) {
                                    return {
                                        ...element,
                                        compareTo: {
                                            ...element.compareTo,
                                            coverLetterDetails:
                                                element.compareTo &&
                                                Object.keys(element.compareTo)
                                                    .length > 0 &&
                                                element.compareTo
                                                    .coverLetterDetails !== null
                                                    ? {
                                                          ...element.compareTo
                                                              .coverLetterDetails,
                                                          sortedVertices:
                                                              Array.from(
                                                                  element
                                                                      .compareTo
                                                                      .coverLetterDetails
                                                                      .sortedVertices ??
                                                                      []
                                                              ).map((o) => ({
                                                                  ...o,
                                                                  isActive:
                                                                      false,
                                                                  isDrawn:
                                                                      getDrawnStatus(
                                                                          o.isDrawn,
                                                                          o.isActive
                                                                      ),
                                                              })),
                                                      }
                                                    : null,
                                            sortedVertices: Array.from(
                                                element.compareTo
                                                    .sortedVertices ?? []
                                            ).map((o) => ({
                                                ...o,
                                                isActive: false,
                                                isDrawn: getDrawnStatus(
                                                    o.isDrawn,
                                                    o.isActive
                                                ),
                                            })),
                                        },
                                        compareWith: {
                                            ...element.compareWith,
                                            coverLetterDetails:
                                                element.compareWith &&
                                                Object.keys(element.compareWith)
                                                    .length > 0 &&
                                                element.compareWith
                                                    .coverLetterDetails !== null
                                                    ? {
                                                          ...element.compareWith
                                                              .coverLetterDetails,
                                                          sortedVertices:
                                                              Array.from(
                                                                  element
                                                                      .compareWith
                                                                      .coverLetterDetails
                                                                      .sortedVertices ??
                                                                      []
                                                              ).map((o) => ({
                                                                  ...o,
                                                                  isActive:
                                                                      false,
                                                                  isDrawn:
                                                                      getDrawnStatus(
                                                                          o.isDrawn,
                                                                          o.isActive
                                                                      ),
                                                              })),
                                                      }
                                                    : null,
                                            sortedVertices: Array.from(
                                                element.compareWith
                                                    .sortedVertices ?? []
                                            ).map((o) => ({
                                                ...o,
                                                isActive: false,
                                                isDrawn: getDrawnStatus(
                                                    o.isDrawn,
                                                    o.isActive
                                                ),
                                            })),
                                        },
                                    };
                                }
                                return element;
                            })
                        );
                    }
                    setOpenDetail(stepData?.result);
                    setSequenceData(stepData);
                    setStep(stepData?.step ?? 0);
                    setSequence(nextStep);
                    // handleReset();
                } else {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { reviewData, ...documentState } = defaultPacketState;

                    const currentreviewedStepData =
                        values.reviewedSteps[currentIndex];

                    if (openDetail === 'Send For Rework') {
                        const solicitor = SolicitorsData.find(
                            (o) =>
                                `${o.name} - ${o.email.id}` ===
                                currentreviewedStepData.solicitor
                        );
                        // lock solicitor to use one person
                        if (solicitor) {
                            const Data = Array.from(
                                Array.from(data).map((element) => {
                                    if (element.sequence === currentSequence) {
                                        return {
                                            ...element,
                                            compareTo: {
                                                ...element.compareTo,
                                                coverLetterDetails:
                                                    element.compareTo &&
                                                    Object.keys(
                                                        element.compareTo
                                                    ).length > 0 &&
                                                    element.compareTo
                                                        .coverLetterDetails !==
                                                        null
                                                        ? {
                                                              ...element
                                                                  .compareTo
                                                                  .coverLetterDetails,
                                                              sortedVertices:
                                                                  Array.from(
                                                                      element
                                                                          .compareTo
                                                                          .coverLetterDetails
                                                                          .sortedVertices ??
                                                                          []
                                                                  ).map(
                                                                      (o) => ({
                                                                          ...o,
                                                                          isActive:
                                                                              false,
                                                                          isDrawn:
                                                                              getDrawnStatus(
                                                                                  o.isDrawn,
                                                                                  o.isActive
                                                                              ),
                                                                      })
                                                                  ),
                                                          }
                                                        : null,
                                                sortedVertices: Array.from(
                                                    element.compareTo
                                                        .sortedVertices ?? []
                                                ).map((o) => ({
                                                    ...o,
                                                    isActive: false,
                                                    isDrawn: getDrawnStatus(
                                                        o.isDrawn,
                                                        o.isActive
                                                    ),
                                                })),
                                            },
                                            compareWith: {
                                                ...element.compareWith,
                                                coverLetterDetails:
                                                    element.compareWith &&
                                                    Object.keys(
                                                        element.compareWith
                                                    ).length > 0 &&
                                                    element.compareWith
                                                        .coverLetterDetails !==
                                                        null
                                                        ? {
                                                              ...element
                                                                  .compareWith
                                                                  .coverLetterDetails,
                                                              sortedVertices:
                                                                  Array.from(
                                                                      element
                                                                          .compareWith
                                                                          .coverLetterDetails
                                                                          .sortedVertices ??
                                                                          []
                                                                  ).map(
                                                                      (o) => ({
                                                                          ...o,
                                                                          isActive:
                                                                              false,
                                                                          isDrawn:
                                                                              getDrawnStatus(
                                                                                  o.isDrawn,
                                                                                  o.isActive
                                                                              ),
                                                                      })
                                                                  ),
                                                          }
                                                        : null,
                                                sortedVertices: Array.from(
                                                    element.compareWith
                                                        .sortedVertices ?? []
                                                ).map((o) => ({
                                                    ...o,
                                                    isActive: false,
                                                    isDrawn: getDrawnStatus(
                                                        o.isDrawn,
                                                        o.isActive
                                                    ),
                                                })),
                                            },
                                            firstReason:
                                                currentreviewedStepData.firstReason,
                                            secondReason:
                                                currentreviewedStepData.secondReason,
                                            firstReasonPreDefined:
                                                currentreviewedStepData.firstReasonPreDefined,
                                            secondReasonPreDefined:
                                                currentreviewedStepData.secondReasonPreDefined,

                                            isFirstDocument:
                                                currentreviewedStepData.isFirstDocument,
                                            isSecondDocument:
                                                currentreviewedStepData.isSecondDocument,
                                            // expiryDate: values.datepickerRework,
                                        };
                                    }
                                    return element;
                                })
                            ).map((el) => ({
                                ...el,
                                solicitorData: solicitor,
                                expiryDate:
                                    currentreviewedStepData.datepickerRework,
                            }));
                            navigation('/single-reviewed', {
                                replace: currentIdx === 0,
                                state: {
                                    reviewData: Data,
                                    documentData: documentState,
                                },
                            });
                        } else {
                            const Data = Array.from(data).map((element) => {
                                if (element.sequence === currentSequence) {
                                    return {
                                        ...element,
                                        compareTo: {
                                            ...element.compareTo,
                                            coverLetterDetails:
                                                element.compareTo &&
                                                Object.keys(element.compareTo)
                                                    .length > 0 &&
                                                element.compareTo
                                                    .coverLetterDetails !== null
                                                    ? {
                                                          ...element.compareTo
                                                              .coverLetterDetails,
                                                          sortedVertices:
                                                              Array.from(
                                                                  element
                                                                      .compareTo
                                                                      .coverLetterDetails
                                                                      .sortedVertices ??
                                                                      []
                                                              ).map((o) => ({
                                                                  ...o,
                                                                  isActive:
                                                                      false,
                                                                  isDrawn:
                                                                      getDrawnStatus(
                                                                          o.isDrawn,
                                                                          o.isActive
                                                                      ),
                                                              })),
                                                      }
                                                    : null,
                                            sortedVertices: Array.from(
                                                element.compareTo
                                                    .sortedVertices ?? []
                                            ).map((o) => ({
                                                ...o,
                                                isActive: false,
                                                isDrawn: getDrawnStatus(
                                                    o.isDrawn,
                                                    o.isActive
                                                ),
                                            })),
                                        },
                                        compareWith: {
                                            ...element.compareWith,
                                            coverLetterDetails:
                                                element.compareWith &&
                                                Object.keys(element.compareWith)
                                                    .length > 0 &&
                                                element.compareWith
                                                    .coverLetterDetails !== null
                                                    ? {
                                                          ...element.compareWith
                                                              .coverLetterDetails,
                                                          sortedVertices:
                                                              Array.from(
                                                                  element
                                                                      .compareWith
                                                                      .coverLetterDetails
                                                                      .sortedVertices ??
                                                                      []
                                                              ).map((o) => ({
                                                                  ...o,
                                                                  isActive:
                                                                      false,
                                                                  isDrawn:
                                                                      getDrawnStatus(
                                                                          o.isDrawn,
                                                                          o.isActive
                                                                      ),
                                                              })),
                                                      }
                                                    : null,
                                            sortedVertices: Array.from(
                                                element.compareWith
                                                    .sortedVertices ?? []
                                            ).map((o) => ({
                                                ...o,
                                                isActive: false,
                                                isDrawn:
                                                    typeof o.isActive ===
                                                        'boolean' &&
                                                    o.isActive === true
                                                        ? o.isActive
                                                        : false,
                                            })),
                                        },
                                        firstReason:
                                            currentreviewedStepData.firstReason,
                                        secondReason:
                                            currentreviewedStepData.secondReason,
                                        firstReasonPreDefined:
                                            currentreviewedStepData.firstReasonPreDefined,
                                        secondReasonPreDefined:
                                            currentreviewedStepData.secondReasonPreDefined,

                                        isFirstDocument:
                                            currentreviewedStepData.isFirstDocument,
                                        isSecondDocument:
                                            currentreviewedStepData.isSecondDocument,
                                        // expiryDate: values.datepickerRework,
                                    };
                                }
                                return element;
                            });
                            navigation('/single-reviewed', {
                                replace: currentIdx === 0,
                                state: {
                                    reviewData: Data,
                                    documentData: documentState,
                                },
                            });
                        }
                    } else if (openDetail === 'Escalated') {
                        const Data = Array.from(data).map((element) => {
                            if (element.sequence === currentSequence) {
                                return {
                                    ...element,
                                    compareTo: {
                                        ...element.compareTo,
                                        coverLetterDetails:
                                            element.compareTo &&
                                            Object.keys(element.compareTo)
                                                .length > 0 &&
                                            element.compareTo
                                                .coverLetterDetails !== null
                                                ? {
                                                      ...element.compareTo
                                                          .coverLetterDetails,
                                                      sortedVertices:
                                                          Array.from(
                                                              element.compareTo
                                                                  .coverLetterDetails
                                                                  .sortedVertices ??
                                                                  []
                                                          ).map((o) => ({
                                                              ...o,
                                                              isActive: false,
                                                              isDrawn:
                                                                  getDrawnStatus(
                                                                      o.isDrawn,
                                                                      o.isActive
                                                                  ),
                                                          })),
                                                  }
                                                : null,
                                        sortedVertices: Array.from(
                                            element.compareTo.sortedVertices ??
                                                []
                                        ).map((o) => ({
                                            ...o,
                                            isActive: false,
                                            isDrawn: getDrawnStatus(
                                                o.isDrawn,
                                                o.isActive
                                            ),
                                        })),
                                    },
                                    compareWith: {
                                        ...element.compareWith,
                                        coverLetterDetails:
                                            element.compareWith &&
                                            Object.keys(element.compareWith)
                                                .length > 0 &&
                                            element.compareWith
                                                .coverLetterDetails !== null
                                                ? {
                                                      ...element.compareWith
                                                          .coverLetterDetails,
                                                      sortedVertices:
                                                          Array.from(
                                                              element
                                                                  .compareWith
                                                                  .coverLetterDetails
                                                                  .sortedVertices ??
                                                                  []
                                                          ).map((o) => ({
                                                              ...o,
                                                              isActive: false,
                                                              isDrawn:
                                                                  getDrawnStatus(
                                                                      o.isDrawn,
                                                                      o.isActive
                                                                  ),
                                                          })),
                                                  }
                                                : null,
                                        sortedVertices: Array.from(
                                            element.compareWith
                                                .sortedVertices ?? []
                                        ).map((o) => ({
                                            ...o,
                                            isActive: false,
                                            isDrawn:
                                                typeof o.isActive ===
                                                    'boolean' &&
                                                o.isActive === true
                                                    ? o.isActive
                                                    : false,
                                        })),
                                    },
                                    escalatedReason:
                                        currentreviewedStepData.escalatedReason,
                                };
                            }
                            return element;
                        });
                        navigation('/single-reviewed', {
                            replace: currentIdx === 0,
                            state: {
                                reviewData: Data,
                                documentData: documentState,
                            },
                        });
                    } else {
                        const Data = Array.from(data).map((element) => {
                            if (element.sequence === currentSequence) {
                                return {
                                    ...element,
                                    compareTo: {
                                        ...element.compareTo,
                                        coverLetterDetails:
                                            element.compareTo &&
                                            Object.keys(element.compareTo)
                                                .length > 0 &&
                                            element.compareTo
                                                .coverLetterDetails !== null
                                                ? {
                                                      ...element.compareTo
                                                          .coverLetterDetails,
                                                      sortedVertices:
                                                          Array.from(
                                                              element.compareTo
                                                                  .coverLetterDetails
                                                                  .sortedVertices ??
                                                                  []
                                                          ).map((o) => ({
                                                              ...o,
                                                              isActive: false,
                                                              isDrawn:
                                                                  getDrawnStatus(
                                                                      o.isDrawn,
                                                                      o.isActive
                                                                  ),
                                                          })),
                                                  }
                                                : null,
                                        sortedVertices: Array.from(
                                            element.compareTo.sortedVertices ??
                                                []
                                        ).map((o) => ({
                                            ...o,
                                            isActive: false,
                                            isDrawn: getDrawnStatus(
                                                o.isDrawn,
                                                o.isActive
                                            ),
                                        })),
                                    },
                                    compareWith: {
                                        ...element.compareWith,
                                        coverLetterDetails:
                                            element.compareWith &&
                                            Object.keys(element.compareWith)
                                                .length > 0 &&
                                            element.compareWith
                                                .coverLetterDetails !== null
                                                ? {
                                                      ...element.compareWith
                                                          .coverLetterDetails,
                                                      sortedVertices:
                                                          Array.from(
                                                              element
                                                                  .compareWith
                                                                  .coverLetterDetails
                                                                  .sortedVertices ??
                                                                  []
                                                          ).map((o) => ({
                                                              ...o,
                                                              isActive: false,
                                                              isDrawn:
                                                                  getDrawnStatus(
                                                                      o.isDrawn,
                                                                      o.isActive
                                                                  ),
                                                          })),
                                                  }
                                                : null,
                                        sortedVertices: Array.from(
                                            element.compareWith
                                                .sortedVertices ?? []
                                        ).map((o) => ({
                                            ...o,
                                            isActive: false,
                                            isDrawn: getDrawnStatus(
                                                o.isDrawn,
                                                o.isActive
                                            ),
                                        })),
                                    },
                                };
                            }
                            return element;
                        });
                        navigation('/single-reviewed', {
                            replace: currentIdx === 0,
                            state: {
                                reviewData: Data,
                                documentData: documentState,
                            },
                        });
                    }
                }
            }
        }, [
            SolicitorsData,
            currentIdx,
            currentSequence,
            data,
            defaultPacketState,
            getDrawnStatus,
            navigation,
            onStepClicked,
            openDetail,
            setFieldTouched,
            validateForm,
            values.reviewedSteps,
        ]);

        /**
         * action when step processed to the previously stepData
         */
        const actionOnProcessedToStepPrev = React.useCallback(() => {
            const prevStepSequenceFromData = Array.from(data)
                .sort((a, b) => a.sequence - b.sequence)
                .findLast(
                    (el) =>
                        el.sequence <= currentSequence - 1 &&
                        // eslint-disable-next-line no-underscore-dangle
                        el.stack.knownInstance()
                );
            const prevStep = prevStepSequenceFromData
                ? prevStepSequenceFromData.sequence
                : 0;
            const currentIndex = data.findIndex(
                (o) => o.sequence === currentSequence
            );
            if (typeof onStepClicked === 'function') {
                onStepClicked();
            }

            if (
                prevStep > 0 &&
                data.some((o) => o.sequence < currentSequence) &&
                typeof currentIndex === 'number' &&
                currentIndex >= 0
            ) {
                const stepData = data.find((o) => o.sequence === prevStep);

                const currentreviewedStepData =
                    values.reviewedSteps[currentIndex];
                // const fieldStepHelper = getFieldHelpers('reviewedSteps');
                if (openDetail === 'Send For Rework') {
                    const solicitor = SolicitorsData.find(
                        (o) =>
                            `${o.name} - ${o.email.id}` ===
                            currentreviewedStepData.solicitor
                    );
                    // lock solicitor to use one person
                    if (solicitor) {
                        setData(
                            Array.from(
                                Array.from(data).map((element) => {
                                    if (element.sequence === currentSequence) {
                                        return {
                                            ...element,
                                            compareTo: {
                                                ...element.compareTo,
                                                coverLetterDetails:
                                                    element.compareTo &&
                                                    Object.keys(
                                                        element.compareTo
                                                    ).length > 0 &&
                                                    element.compareTo
                                                        .coverLetterDetails !==
                                                        null
                                                        ? {
                                                              ...element
                                                                  .compareTo
                                                                  .coverLetterDetails,
                                                              sortedVertices:
                                                                  Array.from(
                                                                      element
                                                                          .compareTo
                                                                          .coverLetterDetails
                                                                          .sortedVertices ??
                                                                          []
                                                                  ).map(
                                                                      (o) => ({
                                                                          ...o,
                                                                          isActive:
                                                                              false,
                                                                          isDrawn:
                                                                              getDrawnStatus(
                                                                                  o.isDrawn,
                                                                                  o.isActive
                                                                              ),
                                                                      })
                                                                  ),
                                                          }
                                                        : null,
                                                sortedVertices: Array.from(
                                                    element.compareTo
                                                        .sortedVertices ?? []
                                                ).map((o) => ({
                                                    ...o,
                                                    isActive: false,
                                                    isDrawn: getDrawnStatus(
                                                        o.isDrawn,
                                                        o.isActive
                                                    ),
                                                })),
                                            },
                                            compareWith: {
                                                ...element.compareWith,
                                                coverLetterDetails:
                                                    element.compareWith &&
                                                    Object.keys(
                                                        element.compareWith
                                                    ).length > 0 &&
                                                    element.compareWith
                                                        .coverLetterDetails !==
                                                        null
                                                        ? {
                                                              ...element
                                                                  .compareWith
                                                                  .coverLetterDetails,
                                                              sortedVertices:
                                                                  Array.from(
                                                                      element
                                                                          .compareWith
                                                                          .coverLetterDetails
                                                                          .sortedVertices ??
                                                                          []
                                                                  ).map(
                                                                      (o) => ({
                                                                          ...o,
                                                                          isActive:
                                                                              false,
                                                                          isDrawn:
                                                                              getDrawnStatus(
                                                                                  o.isDrawn,
                                                                                  o.isActive
                                                                              ),
                                                                      })
                                                                  ),
                                                          }
                                                        : null,
                                                sortedVertices: Array.from(
                                                    element.compareWith
                                                        .sortedVertices ?? []
                                                ).map((o) => ({
                                                    ...o,
                                                    isActive: false,
                                                    isDrawn: getDrawnStatus(
                                                        o.isDrawn,
                                                        o.isActive
                                                    ),
                                                })),
                                            },
                                            // solicitorData: solicitor,
                                            firstReason:
                                                currentreviewedStepData.firstReason,
                                            secondReason:
                                                currentreviewedStepData.secondReason,
                                            firstReasonPreDefined:
                                                currentreviewedStepData.firstReasonPreDefined,
                                            secondReasonPreDefined:
                                                currentreviewedStepData.secondReasonPreDefined,

                                            isFirstDocument:
                                                currentreviewedStepData.isFirstDocument,
                                            isSecondDocument:
                                                currentreviewedStepData.isSecondDocument,
                                            // expiryDate: values.datepickerRework,
                                        };
                                    }
                                    return element;
                                })
                            ).map((el) => ({
                                ...el,
                                solicitorData: solicitor,
                                expiryDate:
                                    currentreviewedStepData.datepickerRework,
                            }))
                        );
                    } else {
                        setData(
                            Array.from(data).map((element) => {
                                if (element.sequence === currentSequence) {
                                    return {
                                        ...element,
                                        compareTo: {
                                            ...element.compareTo,
                                            coverLetterDetails:
                                                element.compareTo &&
                                                Object.keys(element.compareTo)
                                                    .length > 0 &&
                                                element.compareTo
                                                    .coverLetterDetails !== null
                                                    ? {
                                                          ...element.compareTo
                                                              .coverLetterDetails,
                                                          sortedVertices:
                                                              Array.from(
                                                                  element
                                                                      .compareTo
                                                                      .coverLetterDetails
                                                                      .sortedVertices ??
                                                                      []
                                                              ).map((o) => ({
                                                                  ...o,
                                                                  isActive:
                                                                      false,
                                                                  isDrawn:
                                                                      getDrawnStatus(
                                                                          o.isDrawn,
                                                                          o.isActive
                                                                      ),
                                                              })),
                                                      }
                                                    : null,
                                            sortedVertices: Array.from(
                                                element.compareTo
                                                    .sortedVertices ?? []
                                            ).map((o) => ({
                                                ...o,
                                                isActive: false,
                                                isDrawn: getDrawnStatus(
                                                    o.isDrawn,
                                                    o.isActive
                                                ),
                                            })),
                                        },
                                        compareWith: {
                                            ...element.compareWith,
                                            coverLetterDetails:
                                                element.compareWith &&
                                                Object.keys(element.compareWith)
                                                    .length > 0 &&
                                                element.compareWith
                                                    .coverLetterDetails !== null
                                                    ? {
                                                          ...element.compareWith
                                                              .coverLetterDetails,
                                                          sortedVertices:
                                                              Array.from(
                                                                  element
                                                                      .compareWith
                                                                      .coverLetterDetails
                                                                      .sortedVertices ??
                                                                      []
                                                              ).map((o) => ({
                                                                  ...o,
                                                                  isActive:
                                                                      false,
                                                                  isDrawn:
                                                                      getDrawnStatus(
                                                                          o.isDrawn,
                                                                          o.isActive
                                                                      ),
                                                              })),
                                                      }
                                                    : null,
                                            sortedVertices: Array.from(
                                                element.compareWith
                                                    .sortedVertices ?? []
                                            ).map((o) => ({
                                                ...o,
                                                isActive: false,
                                                isDrawn: getDrawnStatus(
                                                    o.isDrawn,
                                                    o.isActive
                                                ),
                                            })),
                                        },
                                        firstReason:
                                            currentreviewedStepData.firstReason,
                                        secondReason:
                                            currentreviewedStepData.secondReason,
                                        firstReasonPreDefined:
                                            currentreviewedStepData.firstReasonPreDefined,
                                        secondReasonPreDefined:
                                            currentreviewedStepData.secondReasonPreDefined,

                                        isFirstDocument:
                                            currentreviewedStepData.isFirstDocument,
                                        isSecondDocument:
                                            currentreviewedStepData.isSecondDocument,
                                        // expiryDate: values.datepickerRework,
                                    };
                                }
                                return element;
                            })
                        );
                    }
                } else if (openDetail === 'Escalated') {
                    setData(
                        Array.from(data).map((element) => {
                            if (element.sequence === currentSequence) {
                                return {
                                    ...element,
                                    compareTo: {
                                        ...element.compareTo,
                                        coverLetterDetails:
                                            element.compareTo &&
                                            Object.keys(element.compareTo)
                                                .length > 0 &&
                                            element.compareTo
                                                .coverLetterDetails !== null
                                                ? {
                                                      ...element.compareTo
                                                          .coverLetterDetails,
                                                      sortedVertices:
                                                          Array.from(
                                                              element.compareTo
                                                                  .coverLetterDetails
                                                                  .sortedVertices ??
                                                                  []
                                                          ).map((o) => ({
                                                              ...o,
                                                              isActive: false,
                                                              isDrawn:
                                                                  getDrawnStatus(
                                                                      o.isDrawn,
                                                                      o.isActive
                                                                  ),
                                                          })),
                                                  }
                                                : null,
                                        sortedVertices: Array.from(
                                            element.compareTo.sortedVertices ??
                                                []
                                        ).map((o) => ({
                                            ...o,
                                            isActive: false,
                                            isDrawn: getDrawnStatus(
                                                o.isDrawn,
                                                o.isActive
                                            ),
                                        })),
                                    },
                                    compareWith: {
                                        ...element.compareWith,
                                        coverLetterDetails:
                                            element.compareWith &&
                                            Object.keys(element.compareWith)
                                                .length > 0 &&
                                            element.compareWith
                                                .coverLetterDetails !== null
                                                ? {
                                                      ...element.compareWith
                                                          .coverLetterDetails,
                                                      sortedVertices:
                                                          Array.from(
                                                              element
                                                                  .compareWith
                                                                  .coverLetterDetails
                                                                  .sortedVertices ??
                                                                  []
                                                          ).map((o) => ({
                                                              ...o,
                                                              isActive: false,
                                                              isDrawn:
                                                                  getDrawnStatus(
                                                                      o.isDrawn,
                                                                      o.isActive
                                                                  ),
                                                          })),
                                                  }
                                                : null,
                                        sortedVertices: Array.from(
                                            element.compareWith
                                                .sortedVertices ?? []
                                        ).map((o) => ({
                                            ...o,
                                            isActive: false,
                                            isDrawn: getDrawnStatus(
                                                o.isDrawn,
                                                o.isActive
                                            ),
                                        })),
                                    },
                                    escalatedReason:
                                        currentreviewedStepData.escalatedReason,
                                };
                            }
                            return element;
                        })
                    );
                } else {
                    setData(
                        Array.from(data).map((element) => {
                            if (element.sequence === currentSequence) {
                                return {
                                    ...element,
                                    compareTo: {
                                        ...element.compareTo,
                                        coverLetterDetails:
                                            element.compareTo &&
                                            Object.keys(element.compareTo)
                                                .length > 0 &&
                                            element.compareTo
                                                .coverLetterDetails !== null
                                                ? {
                                                      ...element.compareTo
                                                          .coverLetterDetails,
                                                      sortedVertices:
                                                          Array.from(
                                                              element.compareTo
                                                                  .coverLetterDetails
                                                                  .sortedVertices ??
                                                                  []
                                                          ).map((o) => ({
                                                              ...o,
                                                              isActive: false,
                                                              isDrawn:
                                                                  getDrawnStatus(
                                                                      o.isDrawn,
                                                                      o.isActive
                                                                  ),
                                                          })),
                                                  }
                                                : null,
                                        sortedVertices: Array.from(
                                            element.compareTo.sortedVertices ??
                                                []
                                        ).map((o) => ({
                                            ...o,
                                            isActive: false,
                                            isDrawn: getDrawnStatus(
                                                o.isDrawn,
                                                o.isActive
                                            ),
                                        })),
                                    },
                                    compareWith: {
                                        ...element.compareWith,
                                        coverLetterDetails:
                                            element.compareWith &&
                                            Object.keys(element.compareWith)
                                                .length > 0 &&
                                            element.compareWith
                                                .coverLetterDetails !== null
                                                ? {
                                                      ...element.compareWith
                                                          .coverLetterDetails,
                                                      sortedVertices:
                                                          Array.from(
                                                              element
                                                                  .compareWith
                                                                  .coverLetterDetails
                                                                  .sortedVertices ??
                                                                  []
                                                          ).map((o) => ({
                                                              ...o,
                                                              isActive: false,
                                                              isDrawn:
                                                                  getDrawnStatus(
                                                                      o.isDrawn,
                                                                      o.isActive
                                                                  ),
                                                          })),
                                                  }
                                                : null,
                                        sortedVertices: Array.from(
                                            element.compareWith
                                                .sortedVertices ?? []
                                        ).map((o) => ({
                                            ...o,
                                            isActive: false,
                                            isDrawn: getDrawnStatus(
                                                o.isDrawn,
                                                o.isActive
                                            ),
                                        })),
                                    },
                                };
                            }
                            return element;
                        })
                    );
                }
                setOpenDetail(stepData?.result);
                setSequenceData(stepData);
                setStep(stepData?.step ?? 0);
                setSequence(prevStep);
                // handleReset();
            } else {
                setActiveBackNavigation(true);

                ManagePacketService.patchOperatorAction({
                    open: false,
                    packetId,
                })
                    .then(() => {
                        // Utils.handleSuccessResponse(response);

                        if (isHistoricRouter) {
                            setActiveBackNavigation(false);
                            navigation('/manage-packets', {
                                replace: true,
                                state: {
                                    isOpen: false,
                                },
                            });
                        } else {
                            dispatch({
                                type: LOCATION_CHANGE,
                                payload: {
                                    location: {
                                        ...location,
                                        state: {
                                            isOpen: false,
                                        },
                                    },
                                    action: Action.Replace,
                                },
                            });
                            setActiveBackNavigation(false);
                            navigation(-1);
                        }
                    })
                    .catch((response) => {
                        setActiveBackNavigation(false);
                        Utils.handleErrorResponse(response);
                    });
            }
        }, [
            SolicitorsData,
            currentSequence,
            data,
            dispatch,
            getDrawnStatus,
            isHistoricRouter,
            location,
            navigation,
            onStepClicked,
            openDetail,
            packetId,
            values.reviewedSteps,
        ]);

        const handleCloseButton = React.useCallback(() => {
            setActiveBackNavigation(true);

            ManagePacketService.patchOperatorAction({
                open: false,
                packetId,
            })
                .then(() => {
                    // Utils.handleSuccessResponse(response);

                    if (isHistoricRouter) {
                        setActiveBackNavigation(false);
                        navigation('/manage-packets', {
                            replace: true,
                            state: {
                                isOpen: false,
                            },
                        });
                    } else {
                        dispatch({
                            type: LOCATION_CHANGE,
                            payload: {
                                location: {
                                    ...location,
                                    state: {
                                        isOpen: false,
                                    },
                                },
                                action: Action.Replace,
                            },
                        });
                        setActiveBackNavigation(false);
                        navigation(-1);
                    }
                })
                .catch((response) => {
                    setActiveBackNavigation(false);
                    Utils.handleErrorResponse(response);
                });
        }, [dispatch, isHistoricRouter, location, navigation, packetId]);

        /**
         * Action when step is changing validation categories
         * @param {ParamDataTypeHere} value - categories to select
         * @param {number} stepNumber - step number
         */
        const actionOnTaskDetailToChangeValidation = React.useCallback(
            (
                value:
                    | 'Correct'
                    | 'N/A'
                    | 'Send For Rework'
                    | 'Document Missing'
                    | 'Escalated'
                    | 'Hand Signed'
                    | 'Sign Not Verified',
                stepNumber: number
            ) => {
                const rightCategory = currentSequenceData?.compareTo.category;
                const leftCategory = currentSequenceData?.compareWith.category;
                const isAlreadyStepable = data.some(
                    (t) => t.sequence > stepNumber && t.isRead
                );
                setOpenDetail(value);
                setData(
                    Array.from(data).map((el, index) => {
                        const isReplaceableRightCompareToCategory =
                            (typeof el.compareTo.documentId === 'undefined' &&
                                el.compareTo.category === rightCategory) ||
                            (typeof currentSequenceData?.compareTo
                                .documentId !== 'undefined' &&
                                el.compareTo.documentId ===
                                    currentSequenceData?.compareTo.documentId &&
                                ((currentSequenceData?.compareTo.documentId ===
                                    null &&
                                    el.compareTo.category === rightCategory) ||
                                    currentSequenceData?.compareTo
                                        .documentId !== null));
                        const isReplaceableRightCompareWithCategory =
                            (typeof el.compareWith.documentId === 'undefined' &&
                                el.compareWith.category === rightCategory) ||
                            (typeof currentSequenceData?.compareTo
                                .documentId !== 'undefined' &&
                                el.compareWith.documentId ===
                                    currentSequenceData?.compareTo.documentId &&
                                ((currentSequenceData?.compareTo.documentId ===
                                    null &&
                                    el.compareWith.category ===
                                        rightCategory) ||
                                    currentSequenceData?.compareTo.documentId));

                        const isReplaceableLeftCompareWithCategory =
                            (typeof el.compareWith.documentId === 'undefined' &&
                                el.compareWith.category === leftCategory) ||
                            (typeof currentSequenceData?.compareWith
                                .documentId !== 'undefined' &&
                                el.compareWith.documentId ===
                                    currentSequenceData?.compareWith
                                        .documentId &&
                                ((currentSequenceData?.compareWith
                                    .documentId === null &&
                                    el.compareWith.category === leftCategory) ||
                                    currentSequenceData?.compareWith
                                        .documentId !== null));

                        const isReplaceableLeftCompareToCategory =
                            (typeof el.compareTo.documentId === 'undefined' &&
                                el.compareTo.category === leftCategory) ||
                            (typeof currentSequenceData?.compareWith
                                .documentId !== 'undefined' &&
                                el.compareTo.documentId ===
                                    currentSequenceData?.compareWith
                                        .documentId &&
                                ((currentSequenceData?.compareWith
                                    .documentId === null &&
                                    el.compareTo.category === leftCategory) ||
                                    currentSequenceData?.compareWith
                                        .documentId !== null));

                        const fieldRightHelper = getFieldHelpers(
                            `reviewedSteps[${index}].secondReason`
                        );
                        const fieldRightReasonHelper = getFieldHelpers(
                            `reviewedSteps[${index}].secondReasonPreDefined`
                        );

                        const fieldLeftHelper = getFieldHelpers(
                            `reviewedSteps[${index}].firstReason`
                        );
                        const fieldLeftReasonHelper = getFieldHelpers(
                            `reviewedSteps[${index}].firstReasonPreDefined`
                        );

                        if (
                            (isReplaceableLeftCompareWithCategory ||
                                isReplaceableLeftCompareToCategory) &&
                            value !== 'Send For Rework' &&
                            el.isRead === false &&
                            isAlreadyStepable === false
                        ) {
                            if (isReplaceableLeftCompareWithCategory) {
                                fieldLeftHelper.setValue('', false);
                                fieldLeftReasonHelper.setValue('', false);
                            } else {
                                fieldRightHelper.setValue('', false);
                                fieldRightReasonHelper.setValue('', false);
                            }
                        }
                        if (
                            (isReplaceableRightCompareWithCategory ||
                                isReplaceableRightCompareToCategory) &&
                            value !== 'Send For Rework' &&
                            el.isRead === false &&
                            isAlreadyStepable === false
                        ) {
                            if (isReplaceableRightCompareWithCategory) {
                                fieldLeftHelper.setValue('', false);
                                fieldLeftReasonHelper.setValue('', false);
                            } else {
                                fieldRightHelper.setValue('', false);
                                fieldRightReasonHelper.setValue('', false);
                            }
                        }

                        if (el.sequence === stepNumber) {
                            return {
                                ...el,
                                result: value,
                                isFirstDocument:
                                    value !== 'Send For Rework'
                                        ? false
                                        : el.isFirstDocument,
                                isSecondDocument:
                                    value !== 'Send For Rework'
                                        ? false
                                        : el.isSecondDocument,
                                firstReason:
                                    value !== 'Send For Rework'
                                        ? ''
                                        : el.firstReason,
                                secondReason:
                                    value !== 'Send For Rework'
                                        ? ''
                                        : el.secondReason,
                                secondReasonPreDefined:
                                    value !== 'Send For Rework'
                                        ? ''
                                        : el.secondReasonPreDefined,
                                firstReasonPreDefined:
                                    value !== 'Send For Rework'
                                        ? ''
                                        : el.firstReasonPreDefined,
                            };
                        }

                        return {
                            ...el,
                            isFirstDocument:
                                (isReplaceableLeftCompareWithCategory ||
                                    isReplaceableRightCompareWithCategory) &&
                                value !== 'Send For Rework' &&
                                el.isRead === false &&
                                isAlreadyStepable === false
                                    ? false
                                    : el.isFirstDocument,
                            isSecondDocument:
                                (isReplaceableLeftCompareToCategory ||
                                    isReplaceableRightCompareToCategory) &&
                                value !== 'Send For Rework' &&
                                el.isRead === false &&
                                isAlreadyStepable === false
                                    ? false
                                    : el.isSecondDocument,
                            firstReason:
                                (isReplaceableLeftCompareWithCategory ||
                                    isReplaceableRightCompareWithCategory) &&
                                value !== 'Send For Rework' &&
                                el.isRead === false &&
                                isAlreadyStepable === false
                                    ? ''
                                    : el.firstReason,
                            secondReason:
                                (isReplaceableLeftCompareToCategory ||
                                    isReplaceableRightCompareToCategory) &&
                                value !== 'Send For Rework'
                                    ? ''
                                    : el.secondReason,
                            secondReasonPreDefined:
                                (isReplaceableLeftCompareToCategory ||
                                    isReplaceableRightCompareToCategory) &&
                                value !== 'Send For Rework' &&
                                el.isRead === false &&
                                isAlreadyStepable === false
                                    ? ''
                                    : el.secondReasonPreDefined,
                            firstReasonPreDefined:
                                (isReplaceableLeftCompareWithCategory ||
                                    isReplaceableRightCompareWithCategory) &&
                                value !== 'Send For Rework' &&
                                el.isRead === false &&
                                isAlreadyStepable === false
                                    ? ''
                                    : el.firstReasonPreDefined,
                        };
                    })
                );
                if (currentSequenceData) {
                    setSequenceData({
                        ...currentSequenceData,
                        result: value,
                        isFirstDocument:
                            value !== 'Send For Rework'
                                ? false
                                : currentSequenceData.isFirstDocument,
                        isSecondDocument:
                            value !== 'Send For Rework'
                                ? false
                                : currentSequenceData.isSecondDocument,
                        // firstReason:
                        //     value !== 'Send For Rework'
                        //         ? ''
                        //         : currentSequenceData.firstReason,
                        // secondReason:
                        //     value !== 'Send For Rework'
                        //         ? ''
                        //         : currentSequenceData.secondReason,
                        // secondReasonPreDefined:
                        //     value !== 'Send For Rework'
                        //         ? ''
                        //         : currentSequenceData.secondReasonPreDefined,
                        // firstReasonPreDefined:
                        //     value !== 'Send For Rework'
                        //         ? ''
                        //         : currentSequenceData.firstReasonPreDefined,
                    });
                }
            },
            [currentSequenceData, data, getFieldHelpers]
        );

        /**
         * Handle for document first choice in sent to rework form
         * @param {React.SyntheticEvent} event - event when first choice is checked
         * @param {boolean} checked
         */
        const handleCheckFirstDocument = React.useCallback(
            (event: React.SyntheticEvent<Element, Event>, checked: boolean) => {
                const currentIndex = data.findIndex(
                    (o) => o.sequence === currentSequence
                );
                const currentReviewedStep = values.reviewedSteps[currentIndex];

                const leftCategory = currentSequenceData?.compareWith.category;
                if (typeof currentIndex === 'number' && currentIndex >= 0) {
                    // const firstReasonField = getFieldHelpers(
                    //     `reviewedSteps[${currentIndex}].firstReason`
                    // );

                    const fieldHelper = getFieldHelpers(
                        `reviewedSteps[${currentIndex}].isFirstDocument`
                    );
                    // if (!checked) {
                    //     firstReasonField.setValue('');
                    // }
                    setData(
                        Array.from(data).map((el, index) => {
                            const isReplaceableLeftCompareWithCategory =
                                (typeof el.compareWith.documentId ===
                                    'undefined' &&
                                    el.compareWith.category === leftCategory) ||
                                (typeof currentSequenceData?.compareWith
                                    .documentId !== 'undefined' &&
                                    el.compareWith.documentId ===
                                        currentSequenceData?.compareWith
                                            .documentId &&
                                    ((currentSequenceData?.compareWith
                                        .documentId === null &&
                                        el.compareWith.category ===
                                            leftCategory) ||
                                        currentSequenceData?.compareWith
                                            .documentId !== null));

                            const isReplaceableLeftCompareToCategory =
                                (typeof el.compareTo.documentId ===
                                    'undefined' &&
                                    el.compareTo.category === leftCategory) ||
                                (typeof currentSequenceData?.compareWith
                                    .documentId !== 'undefined' &&
                                    el.compareTo.documentId ===
                                        currentSequenceData?.compareWith
                                            .documentId &&
                                    ((currentSequenceData?.compareWith
                                        .documentId === null &&
                                        el.compareTo.category ===
                                            leftCategory) ||
                                        currentSequenceData?.compareWith
                                            .documentId !== null));

                            const fieldRightHelper = getFieldHelpers(
                                `reviewedSteps[${index}].secondReason`
                            );
                            const fieldRightReasonHelper = getFieldHelpers(
                                `reviewedSteps[${index}].secondReasonPreDefined`
                            );

                            const fieldLeftHelper = getFieldHelpers(
                                `reviewedSteps[${index}].firstReason`
                            );
                            const fieldLeftReasonHelper = getFieldHelpers(
                                `reviewedSteps[${index}].firstReasonPreDefined`
                            );

                            if (
                                (isReplaceableLeftCompareWithCategory ||
                                    isReplaceableLeftCompareToCategory) &&
                                currentSequenceData?.result ===
                                    'Send For Rework' &&
                                el.isRead === false
                            ) {
                                if (isReplaceableLeftCompareWithCategory) {
                                    fieldLeftHelper.setValue(
                                        currentReviewedStep.firstReason,
                                        false
                                    );
                                    fieldLeftReasonHelper.setValue(
                                        currentReviewedStep.firstReasonPreDefined,
                                        false
                                    );
                                } else {
                                    fieldRightHelper.setValue(
                                        currentReviewedStep.firstReason,
                                        false
                                    );
                                    fieldRightReasonHelper.setValue(
                                        currentReviewedStep.firstReasonPreDefined,
                                        false
                                    );
                                }
                            }

                            return {
                                ...el,
                                firstReason:
                                    isReplaceableLeftCompareWithCategory &&
                                    currentSequenceData?.result ===
                                        'Send For Rework' &&
                                    el.isRead === false
                                        ? currentReviewedStep.firstReason
                                        : el.firstReason,
                                secondReason:
                                    isReplaceableLeftCompareToCategory &&
                                    currentSequenceData?.result ===
                                        'Send For Rework' &&
                                    el.isRead === false
                                        ? currentReviewedStep.firstReason
                                        : el.secondReason,
                                secondReasonPreDefined:
                                    isReplaceableLeftCompareToCategory &&
                                    currentSequenceData?.result ===
                                        'Send For Rework' &&
                                    el.isRead === false
                                        ? currentReviewedStep.firstReasonPreDefined
                                        : el.secondReasonPreDefined,
                                firstReasonPreDefined:
                                    isReplaceableLeftCompareWithCategory &&
                                    currentSequenceData?.result ===
                                        'Send For Rework' &&
                                    el.isRead === false
                                        ? currentReviewedStep.firstReasonPreDefined
                                        : el.firstReasonPreDefined,
                            };
                        })
                    );
                    fieldHelper.setValue(checked);
                    setCheckedFirstDocument(checked);
                }
            },
            [currentSequence, data, getFieldHelpers]
        );

        /**
         * Handle for document second choice in sent to rework form
         * @param {React.SyntheticEvent} event - event when second choice is checked
         * @param {boolean} checked
         */
        const handleCheckSecondDocument = React.useCallback(
            (event: React.SyntheticEvent<Element, Event>, checked: boolean) => {
                const currentIndex = data.findIndex(
                    (o) => o.sequence === currentSequence
                );
                const currentReviewedStep = values.reviewedSteps[currentIndex];
                const rightCategory = currentSequenceData?.compareTo.category;

                if (typeof currentIndex === 'number' && currentIndex >= 0) {
                    // const secondReasonField = getFieldHelpers(
                    //     `reviewedSteps[${currentSequence - 1}].secondReason`
                    // );
                    const fieldHelper = getFieldHelpers(
                        `reviewedSteps[${currentIndex}].isSecondDocument`
                    );
                    // if (!checked) {
                    //     secondReasonField.setValue('');
                    // }
                    setData(
                        Array.from(data).map((el, index) => {
                            const isReplaceableRightCompareToCategory =
                                (typeof el.compareTo.documentId ===
                                    'undefined' &&
                                    el.compareTo.category === rightCategory) ||
                                (typeof currentSequenceData?.compareTo
                                    .documentId !== 'undefined' &&
                                    el.compareTo.documentId ===
                                        currentSequenceData?.compareTo
                                            .documentId &&
                                    ((currentSequenceData?.compareTo
                                        .documentId === null &&
                                        el.compareTo.category ===
                                            rightCategory) ||
                                        currentSequenceData?.compareTo
                                            .documentId !== null));
                            const isReplaceableRightCompareWithCategory =
                                (typeof el.compareWith.documentId ===
                                    'undefined' &&
                                    el.compareWith.category ===
                                        rightCategory) ||
                                (typeof currentSequenceData?.compareTo
                                    .documentId !== 'undefined' &&
                                    el.compareWith.documentId ===
                                        currentSequenceData?.compareTo
                                            .documentId &&
                                    ((currentSequenceData?.compareTo
                                        .documentId === null &&
                                        el.compareWith.category ===
                                            rightCategory) ||
                                        currentSequenceData?.compareTo
                                            .documentId));

                            const fieldRightHelper = getFieldHelpers(
                                `reviewedSteps[${index}].secondReason`
                            );
                            const fieldRightReasonHelper = getFieldHelpers(
                                `reviewedSteps[${index}].secondReasonPreDefined`
                            );

                            const fieldLeftHelper = getFieldHelpers(
                                `reviewedSteps[${index}].firstReason`
                            );
                            const fieldLeftReasonHelper = getFieldHelpers(
                                `reviewedSteps[${index}].firstReasonPreDefined`
                            );

                            if (
                                (isReplaceableRightCompareWithCategory ||
                                    isReplaceableRightCompareToCategory) &&
                                currentSequenceData?.result ===
                                    'Send For Rework' &&
                                el.isRead === false
                            ) {
                                if (isReplaceableRightCompareWithCategory) {
                                    fieldLeftHelper.setValue(
                                        currentReviewedStep.secondReason,
                                        false
                                    );
                                    fieldLeftReasonHelper.setValue(
                                        currentReviewedStep.secondReasonPreDefined,
                                        false
                                    );
                                } else {
                                    fieldRightHelper.setValue(
                                        currentReviewedStep.secondReason,
                                        false
                                    );
                                    fieldRightReasonHelper.setValue(
                                        currentReviewedStep.secondReasonPreDefined,
                                        false
                                    );
                                }
                            }

                            return {
                                ...el,
                                firstReason:
                                    isReplaceableRightCompareWithCategory &&
                                    currentSequenceData?.result ===
                                        'Send For Rework' &&
                                    el.isRead === false
                                        ? currentReviewedStep.secondReason
                                        : el.firstReason,
                                secondReason:
                                    isReplaceableRightCompareToCategory &&
                                    currentSequenceData?.result ===
                                        'Send For Rework' &&
                                    el.isRead === false
                                        ? currentReviewedStep.secondReason
                                        : el.secondReason,
                                secondReasonPreDefined:
                                    isReplaceableRightCompareToCategory &&
                                    currentSequenceData?.result ===
                                        'Send For Rework' &&
                                    el.isRead === false
                                        ? currentReviewedStep.secondReasonPreDefined
                                        : el.secondReasonPreDefined,
                                firstReasonPreDefined:
                                    isReplaceableRightCompareWithCategory &&
                                    currentSequenceData?.result ===
                                        'Send For Rework' &&
                                    el.isRead === false
                                        ? currentReviewedStep.secondReasonPreDefined
                                        : el.firstReasonPreDefined,
                            };
                        })
                    );
                    fieldHelper.setValue(checked);
                    setCheckedSecondDocument(checked);
                }
            },
            [currentSequence, data, getFieldHelpers]
        );

        React.useImperativeHandle(
            ref,
            () => ({
                setLeftJSONPaths: (paths) => {
                    const changedData = Array.from(data).map((element) => {
                        if (element.sequence === currentSequence) {
                            return {
                                ...element,
                                compareWith: {
                                    ...element.compareWith,
                                    paths,
                                },
                            };
                        }
                        return {
                            ...element,
                        };
                    });
                    setData(changedData);
                    setSequenceData(
                        changedData.find((o) => o.sequence === currentSequence)
                    );
                },
                setRightJSONPaths: (paths) => {
                    const changedData = Array.from(data).map((element) => {
                        if (element.sequence === currentSequence) {
                            return {
                                ...element,
                                compareTo: {
                                    ...element.compareTo,
                                    paths,
                                },
                            };
                        }
                        return {
                            ...element,
                        };
                    });
                    setData(changedData);
                    setSequenceData(
                        changedData.find((o) => o.sequence === currentSequence)
                    );
                },
                setCheckFirstDocument: (
                    value,
                    message,
                    category,
                    documentId
                ) => {
                    setData(
                        Array.from(data).map((e) => ({
                            ...e,
                            isFirstDocument:
                                (typeof e.compareWith.documentId ===
                                    'undefined' &&
                                    e.compareWith.category === category) ||
                                (typeof documentId !== 'undefined' &&
                                    e.compareWith.documentId === documentId &&
                                    typeof e.compareWith === 'object' &&
                                    Object.keys(e.compareWith).filter(
                                        (o) =>
                                            o !== 'sortedVertices' &&
                                            o !== 'coverLetterDetails'
                                    ).length > 0 &&
                                    ((documentId === null &&
                                        e.compareWith.category === category) ||
                                        documentId !== null))
                                    ? value
                                    : e.isFirstDocument,
                            firstReason:
                                (typeof e.compareWith.documentId ===
                                    'undefined' &&
                                    e.compareWith.category === category) ||
                                (typeof documentId !== 'undefined' &&
                                    e.compareWith.documentId === documentId &&
                                    typeof e.compareWith === 'object' &&
                                    Object.keys(e.compareWith).filter(
                                        (o) =>
                                            o !== 'sortedVertices' &&
                                            o !== 'coverLetterDetails'
                                    ).length > 0 &&
                                    ((documentId === null &&
                                        e.compareWith.category === category) ||
                                        documentId !== null))
                                    ? message
                                    : e.firstReason,
                            isSecondDocument:
                                (typeof e.compareTo.documentId ===
                                    'undefined' &&
                                    e.compareTo.category === category) ||
                                (typeof documentId !== 'undefined' &&
                                    e.compareTo.documentId === documentId &&
                                    typeof e.compareTo === 'object' &&
                                    Object.keys(e.compareTo).filter(
                                        (o) =>
                                            o !== 'sortedVertices' &&
                                            o !== 'coverLetterDetails'
                                    ).length > 0 &&
                                    ((documentId === null &&
                                        e.compareTo.category === category) ||
                                        documentId !== null))
                                    ? value
                                    : e.isSecondDocument,
                            secondReason:
                                (typeof e.compareTo.documentId ===
                                    'undefined' &&
                                    e.compareTo.category === category) ||
                                (typeof documentId !== 'undefined' &&
                                    e.compareTo.documentId === documentId &&
                                    typeof e.compareTo === 'object' &&
                                    Object.keys(e.compareTo).filter(
                                        (o) =>
                                            o !== 'sortedVertices' &&
                                            o !== 'coverLetterDetails'
                                    ).length > 0 &&
                                    ((documentId === null &&
                                        e.compareTo.category === category) ||
                                        documentId !== null))
                                    ? message
                                    : e.secondReason,
                        }))
                    );
                },
                setCheckSecondDocument: (
                    value,
                    message,
                    category,
                    documentId
                ) => {
                    setData(
                        Array.from(data).map((e) => ({
                            ...e,
                            isFirstDocument:
                                (typeof e.compareWith.documentId ===
                                    'undefined' &&
                                    e.compareWith.category === category) ||
                                (typeof documentId !== 'undefined' &&
                                    e.compareWith.documentId === documentId &&
                                    typeof e.compareWith === 'object' &&
                                    Object.keys(e.compareWith).filter(
                                        (o) =>
                                            o !== 'sortedVertices' &&
                                            o !== 'coverLetterDetails'
                                    ).length > 0 &&
                                    ((documentId === null &&
                                        e.compareWith.category === category) ||
                                        documentId !== null))
                                    ? value
                                    : e.isFirstDocument,
                            firstReason:
                                (typeof e.compareWith.documentId ===
                                    'undefined' &&
                                    e.compareWith.category === category) ||
                                (typeof documentId !== 'undefined' &&
                                    e.compareWith.documentId === documentId &&
                                    typeof e.compareWith === 'object' &&
                                    Object.keys(e.compareWith).filter(
                                        (o) =>
                                            o !== 'sortedVertices' &&
                                            o !== 'coverLetterDetails'
                                    ).length > 0 &&
                                    ((documentId === null &&
                                        e.compareWith.category === category) ||
                                        documentId !== null))
                                    ? message
                                    : e.firstReason,
                            isSecondDocument:
                                (typeof e.compareTo.documentId ===
                                    'undefined' &&
                                    e.compareTo.category === category) ||
                                (typeof documentId !== 'undefined' &&
                                    e.compareTo.documentId === documentId &&
                                    typeof e.compareTo === 'object' &&
                                    Object.keys(e.compareTo).filter(
                                        (o) =>
                                            o !== 'sortedVertices' &&
                                            o !== 'coverLetterDetails'
                                    ).length > 0 &&
                                    ((documentId === null &&
                                        e.compareTo.category === category) ||
                                        documentId !== null))
                                    ? value
                                    : e.isSecondDocument,
                            secondReason:
                                (typeof e.compareTo.documentId ===
                                    'undefined' &&
                                    e.compareTo.category === category) ||
                                (typeof documentId !== 'undefined' &&
                                    e.compareTo.documentId === documentId &&
                                    typeof e.compareTo === 'object' &&
                                    Object.keys(e.compareTo).filter(
                                        (o) =>
                                            o !== 'sortedVertices' &&
                                            o !== 'coverLetterDetails'
                                    ).length > 0 &&
                                    ((documentId === null &&
                                        e.compareTo.category === category) ||
                                        documentId !== null))
                                    ? message
                                    : e.secondReason,
                        }))
                    );
                },
                /**
                 * Set reviewed data to overwrite or initialize footer
                 * @param {ReviewPacketData[]} _data - reviewed data
                 * @param {number} _currentSequence - numerical step value
                 * @param {ReviewPacketData} _currentSequenceData - current review step
                 */
                setReviewedData: (
                    _data,

                    _currentSequence,
                    _currentSequenceData
                ) => {
                    setData(_data);

                    setSequence(_currentSequence);
                    setSequenceData(_currentSequenceData);
                    setStep(_currentSequenceData.step);
                    setOpenDetail(_currentSequenceData.result);
                },
                /**
                 * Set packet related data to overwrite or initialize footer
                 * @param {string} _clientId - clientId from packet data
                 * @param {string} _packetId - packetId from actions properites in packet data
                 */
                setPacketdata: (_clientId, _packetId) => {
                    setClientId(_clientId);
                    setPacketId(_packetId);
                },
                /**
                 * update Vertices compareTo in reviewed data
                 * @param {string} corners - coordinate clockwise from top left position
                 * @param {string} pageNumber - page number to update
                 * @param {string} ratio - ratio to scale to original size corners
                 */
                updateCompareToVerticesData: (corners, pageNumber, ratio) => {
                    const changedData = Array.from(data).map((element) => {
                        if (element.sequence === currentSequence) {
                            return {
                                ...element,
                                compareTo: {
                                    ...element.compareTo,
                                    userDrawn: true,
                                    coverLetterDetails:
                                        element.compareTo.coverLetterDetails !==
                                        null
                                            ? {
                                                  ...element.compareTo
                                                      .coverLetterDetails,
                                                  sortedVertices: (
                                                      element.compareTo
                                                          .coverLetterDetails
                                                          .sortedVertices ?? []
                                                  ).concat([
                                                      {
                                                          isActive: true,
                                                          pageFinder: false,
                                                          x1:
                                                              corners[0][0] *
                                                              ratio,
                                                          x2:
                                                              corners[1][0] *
                                                              ratio,
                                                          x3:
                                                              corners[2][0] *
                                                              ratio,
                                                          x4:
                                                              corners[3][0] *
                                                              ratio,
                                                          y1:
                                                              corners[0][1] *
                                                              ratio,
                                                          y2:
                                                              corners[1][1] *
                                                              ratio,
                                                          y3:
                                                              corners[2][1] *
                                                              ratio,
                                                          y4:
                                                              corners[3][1] *
                                                              ratio,
                                                          pageNumber,
                                                          ratio,
                                                      },
                                                  ]),
                                              }
                                            : null,
                                    sortedVertices:
                                        element.compareTo.coverLetterDetails ===
                                        null
                                            ? (
                                                  element.compareTo
                                                      .sortedVertices ?? []
                                              ).concat([
                                                  {
                                                      isActive: true,
                                                      pageFinder: false,
                                                      x1: corners[0][0] * ratio,
                                                      x2: corners[1][0] * ratio,
                                                      x3: corners[2][0] * ratio,
                                                      x4: corners[3][0] * ratio,
                                                      y1: corners[0][1] * ratio,
                                                      y2: corners[1][1] * ratio,
                                                      y3: corners[2][1] * ratio,
                                                      y4: corners[3][1] * ratio,
                                                      pageNumber,
                                                      ratio,
                                                  },
                                              ])
                                            : element.compareTo.sortedVertices,
                                },
                            };
                        }
                        return {
                            ...element,
                        };
                    });
                    setData(changedData);
                    setSequenceData(
                        changedData.find((o) => o.sequence === currentSequence)
                    );
                },
                removeVerticesDataWithMissingDocument: (
                    isMissingLeftDocument: boolean,
                    isMissingRightDocument: boolean
                ) => {
                    const changedData = Array.from(data).map((element) => {
                        if (element.sequence === currentSequence) {
                            return {
                                ...element,
                                compareWith: isMissingLeftDocument
                                    ? {
                                          ...element.compareWith,
                                          userDrawn: false,
                                          sortedVertices: [],
                                      }
                                    : element.compareWith,
                                compareTo: isMissingRightDocument
                                    ? {
                                          ...element.compareTo,
                                          userDrawn: false,
                                          sortedVertices: [],
                                      }
                                    : element.compareTo,
                            };
                        }
                        return {
                            ...element,
                        };
                    });
                    setData(changedData);
                    setSequenceData(
                        changedData.find((o) => o.sequence === currentSequence)
                    );
                    return changedData;
                },
                /**
                 * Empties compareTo vertices based on active indice
                 * @param {ParamDataTypeHere} index - indice in vertices.
                 *  @param {ParamDataTypeHere} isSaved - trigger from saved vertices or not.
                 * @return {ReviewData[]} changed review data
                 */
                emptyCompareToVerticesData: (index, isSaved) => {
                    const changedData = Array.from(data).map((element) => {
                        if (element.sequence === currentSequence) {
                            let isRemove = false;
                            let isCounted = 0;
                            const updateVertices =
                                element.compareTo.sortedVertices !== null
                                    ? element.compareTo.sortedVertices.filter(
                                          (o) => {
                                              isCounted +=
                                                  o.isActive === !isSaved ||
                                                  ((typeof o.isActive ===
                                                      'undefined') ===
                                                      true &&
                                                      isSaved === true)
                                                      ? 1
                                                      : 0;
                                              if (
                                                  (isCounted - 1 === index) ===
                                                      false ||
                                                  isRemove
                                              ) {
                                                  return true;
                                              }
                                              isRemove = true;
                                              return false;
                                          }
                                      )
                                    : null;
                            return {
                                ...element,
                                compareTo: {
                                    ...element.compareTo,
                                    userDrawn:
                                        (updateVertices?.length ?? 0) > 0,
                                    sortedVertices: updateVertices,
                                },
                            };
                        }
                        return {
                            ...element,
                        };
                    });

                    setData(changedData);
                    setSequenceData(
                        changedData.find((o) => o.sequence === currentSequence)
                    );
                    return changedData;
                },
                /**
                 * update Vertices compareWith in reviewed data
                 * @param {string} corners - coordinate clockwise from top left position
                 * @param {string} pageNumber - page number to update
                 * @param {string} ratio - ratio to scale to original size corners
                 */
                updateCompareWithVerticesData: (corners, pageNumber, ratio) => {
                    const changedData = Array.from(data).map((element) => {
                        if (element.sequence === currentSequence) {
                            return {
                                ...element,
                                compareWith: {
                                    ...element.compareWith,
                                    userDrawn: true,
                                    coverLetterDetails:
                                        element.compareWith
                                            .coverLetterDetails !== null
                                            ? {
                                                  ...element.compareWith
                                                      .coverLetterDetails,
                                                  sortedVertices: (
                                                      element.compareWith
                                                          .coverLetterDetails
                                                          .sortedVertices ?? []
                                                  ).concat([
                                                      {
                                                          isActive: true,
                                                          pageFinder: false,
                                                          x1:
                                                              corners[0][0] *
                                                              ratio,
                                                          x2:
                                                              corners[1][0] *
                                                              ratio,
                                                          x3:
                                                              corners[2][0] *
                                                              ratio,
                                                          x4:
                                                              corners[3][0] *
                                                              ratio,
                                                          y1:
                                                              corners[0][1] *
                                                              ratio,
                                                          y2:
                                                              corners[1][1] *
                                                              ratio,
                                                          y3:
                                                              corners[2][1] *
                                                              ratio,
                                                          y4:
                                                              corners[3][1] *
                                                              ratio,
                                                          pageNumber,
                                                          ratio,
                                                      },
                                                  ]),
                                              }
                                            : null,
                                    sortedVertices:
                                        element.compareWith
                                            .coverLetterDetails === null
                                            ? (
                                                  element.compareWith
                                                      .sortedVertices ?? []
                                              ).concat([
                                                  {
                                                      isActive: true,
                                                      pageFinder: false,
                                                      x1: corners[0][0] * ratio,
                                                      x2: corners[1][0] * ratio,
                                                      x3: corners[2][0] * ratio,
                                                      x4: corners[3][0] * ratio,
                                                      y1: corners[0][1] * ratio,
                                                      y2: corners[1][1] * ratio,
                                                      y3: corners[2][1] * ratio,
                                                      y4: corners[3][1] * ratio,
                                                      pageNumber,
                                                      ratio,
                                                  },
                                              ])
                                            : element.compareWith
                                                  .sortedVertices,
                                },
                            };
                        }
                        return {
                            ...element,
                        };
                    });
                    setData(changedData);
                    setSequenceData(
                        changedData.find((o) => o.sequence === currentSequence)
                    );
                },
                /**
                 * Empties compareWith vertices based on active indice
                 * @param {ParamDataTypeHere} index - indice in vertices.
                 *  @param {ParamDataTypeHere} isSaved - trigger from saved vertices or not.
                 * @return {ReviewData[]} changed review data
                 */
                emptyCompareWithVerticesData: (index, isSaved) => {
                    const changedData = Array.from(data).map((element) => {
                        if (element.sequence === currentSequence) {
                            let isRemove = false;
                            let isCounted = 0;
                            const updateVertices =
                                element.compareWith.sortedVertices !== null
                                    ? element.compareWith.sortedVertices.filter(
                                          (o) => {
                                              isCounted +=
                                                  o.isActive === !isSaved ||
                                                  ((typeof o.isActive ===
                                                      'undefined') ===
                                                      true &&
                                                      isSaved === true)
                                                      ? 1
                                                      : 0;

                                              if (
                                                  (isCounted - 1 === index) ===
                                                      false ||
                                                  isRemove
                                              ) {
                                                  return true;
                                              }

                                              isRemove = true;
                                              return false;
                                          }
                                      )
                                    : null;

                            return {
                                ...element,
                                compareWith: {
                                    ...element.compareWith,
                                    userDrawn:
                                        (updateVertices?.length ?? 0) > 0,
                                    sortedVertices: updateVertices,
                                },
                            };
                        }
                        return {
                            ...element,
                        };
                    });
                    setData(changedData);
                    setSequenceData(
                        changedData.find((o) => o.sequence === currentSequence)
                    );
                    return changedData;
                },
            }),
            [currentSequence, data]
        );
        const disabledEscalateButton = React.useMemo(() => {
            if (role === 'clientManager') {
                return true;
            }
            if ((isSendToReworkFlag || isPaperDocument) && !isMissingDocument) {
                return true;
            }
            return false;
        }, [isMissingDocument, isPaperDocument, isSendToReworkFlag, role]);
        const handleJumpToAnotherStep = React.useCallback(
            async (val: any) => {
                if (val && val.target && val.target.value) {
                    const stepToParse = val.target.value.replace('Step ', '');
                    const jumpStep = parseInt(stepToParse, 10);
                    const currentIndex = data.findIndex(
                        (o) => o.sequence === currentSequence
                    );
                    if (typeof onChangedDropdown === 'function') {
                        onChangedDropdown(
                            jumpStep === currentSequence ? 'persist' : 'jump'
                        );
                    }
                    const validate = await validateForm()
                        .then((response) => {
                            let validated = true;

                            if (
                                response.reviewedSteps &&
                                typeof currentIndex === 'number' &&
                                currentIndex >= 0
                            ) {
                                const currentSequenceResponse =
                                    response.reviewedSteps[currentIndex];

                                if (
                                    response &&
                                    currentSequenceResponse &&
                                    openDetail === 'Send For Rework' &&
                                    (Object.keys(
                                        currentSequenceResponse
                                    ).includes('solicitor') ||
                                        Object.keys(
                                            currentSequenceResponse
                                        ).includes('datepickerRework') ||
                                        Object.keys(
                                            currentSequenceResponse
                                        ).includes('firstReason') ||
                                        Object.keys(
                                            currentSequenceResponse
                                        ).includes('secondReason') ||
                                        Object.keys(
                                            currentSequenceResponse
                                        ).includes('firstReasonPreDefined') ||
                                        Object.keys(
                                            currentSequenceResponse
                                        ).includes('secondReasonPreDefined'))
                                ) {
                                    setFieldTouched(
                                        `reviewedSteps[${currentIndex}].solicitor`,
                                        true
                                    );
                                    setFieldTouched(
                                        `reviewedSteps[${currentIndex}].datepickerRework`,
                                        true
                                    );
                                    setFieldTouched(
                                        `reviewedSteps[${currentIndex}].firstReason`,
                                        true
                                    );
                                    setFieldTouched(
                                        `reviewedSteps[${currentIndex}].secondReason`,
                                        true
                                    );
                                    setFieldTouched(
                                        `reviewedSteps[${currentIndex}].firstReasonPreDefined`,
                                        true
                                    );
                                    setFieldTouched(
                                        `reviewedSteps[${currentIndex}].secondReasonPreDefined`,
                                        true
                                    );
                                    validated = false;
                                } else if (
                                    response &&
                                    currentSequenceResponse &&
                                    Object.keys(
                                        currentSequenceResponse
                                    ).includes('escalatedReason')
                                ) {
                                    setFieldTouched(
                                        `reviewedSteps[${currentIndex}].escalatedReason`,
                                        true
                                    );
                                    validated = false;
                                }
                            }

                            return validated;
                        })
                        .catch(() => false);

                    if (
                        validate &&
                        typeof currentIndex === 'number' &&
                        currentIndex >= 0
                    ) {
                        if (data.some((o) => o.sequence === jumpStep)) {
                            const stepData = data.find(
                                (o) => o.sequence === jumpStep
                            );

                            const currentreviewedStepData =
                                values.reviewedSteps[currentIndex];

                            if (openDetail === 'Send For Rework') {
                                const solicitor = SolicitorsData.find(
                                    (o) =>
                                        `${o.name} - ${o.email.id}` ===
                                        currentreviewedStepData.solicitor
                                );
                                // lock solicitor to use one person
                                if (solicitor) {
                                    setData(
                                        Array.from(
                                            Array.from(data).map((element) => {
                                                if (
                                                    element.sequence ===
                                                    currentSequence
                                                ) {
                                                    return {
                                                        ...element,
                                                        compareTo: {
                                                            ...element.compareTo,
                                                            coverLetterDetails:
                                                                element.compareTo &&
                                                                Object.keys(
                                                                    element.compareTo
                                                                ).length > 0 &&
                                                                element
                                                                    .compareTo
                                                                    .coverLetterDetails !==
                                                                    null
                                                                    ? {
                                                                          ...element
                                                                              .compareTo
                                                                              .coverLetterDetails,
                                                                          sortedVertices:
                                                                              Array.from(
                                                                                  element
                                                                                      .compareTo
                                                                                      .coverLetterDetails
                                                                                      .sortedVertices ??
                                                                                      []
                                                                              ).map(
                                                                                  (
                                                                                      o
                                                                                  ) => ({
                                                                                      ...o,
                                                                                      isActive:
                                                                                          false,
                                                                                      isDrawn:
                                                                                          getDrawnStatus(
                                                                                              o.isDrawn,
                                                                                              o.isActive
                                                                                          ),
                                                                                  })
                                                                              ),
                                                                      }
                                                                    : null,
                                                            sortedVertices:
                                                                Array.from(
                                                                    element
                                                                        .compareTo
                                                                        .sortedVertices ??
                                                                        []
                                                                ).map((o) => ({
                                                                    ...o,
                                                                    isActive:
                                                                        false,
                                                                    isDrawn:
                                                                        getDrawnStatus(
                                                                            o.isDrawn,
                                                                            o.isActive
                                                                        ),
                                                                })),
                                                        },
                                                        compareWith: {
                                                            ...element.compareWith,
                                                            coverLetterDetails:
                                                                element.compareWith &&
                                                                Object.keys(
                                                                    element.compareWith
                                                                ).length > 0 &&
                                                                element
                                                                    .compareWith
                                                                    .coverLetterDetails !==
                                                                    null
                                                                    ? {
                                                                          ...element
                                                                              .compareWith
                                                                              .coverLetterDetails,
                                                                          sortedVertices:
                                                                              Array.from(
                                                                                  element
                                                                                      .compareWith
                                                                                      .coverLetterDetails
                                                                                      .sortedVertices ??
                                                                                      []
                                                                              ).map(
                                                                                  (
                                                                                      o
                                                                                  ) => ({
                                                                                      ...o,
                                                                                      isActive:
                                                                                          false,
                                                                                      isDrawn:
                                                                                          getDrawnStatus(
                                                                                              o.isDrawn,
                                                                                              o.isActive
                                                                                          ),
                                                                                  })
                                                                              ),
                                                                      }
                                                                    : null,
                                                            sortedVertices:
                                                                Array.from(
                                                                    element
                                                                        .compareWith
                                                                        .sortedVertices ??
                                                                        []
                                                                ).map((o) => ({
                                                                    ...o,
                                                                    isActive:
                                                                        false,
                                                                    isDrawn:
                                                                        getDrawnStatus(
                                                                            o.isDrawn,
                                                                            o.isActive
                                                                        ),
                                                                })),
                                                        },
                                                        firstReason:
                                                            currentreviewedStepData.firstReason,
                                                        secondReason:
                                                            currentreviewedStepData.secondReason,
                                                        firstReasonPreDefined:
                                                            currentreviewedStepData.firstReasonPreDefined,
                                                        secondReasonPreDefined:
                                                            currentreviewedStepData.secondReasonPreDefined,

                                                        isFirstDocument:
                                                            currentreviewedStepData.isFirstDocument,
                                                        isSecondDocument:
                                                            currentreviewedStepData.isSecondDocument,
                                                        // expiryDate: values.datepickerRework,
                                                    };
                                                }
                                                return element;
                                            })
                                        ).map((el) => ({
                                            ...el,
                                            solicitorData: solicitor,
                                            expiryDate:
                                                currentreviewedStepData.datepickerRework,
                                        }))
                                    );
                                    // fieldStepHelper.setValue(
                                    //     Array.from(values.reviewedSteps).map(
                                    //         (element) => ({
                                    //             ...element,
                                    //             solicitorData: solicitor,
                                    //             expiryDate:
                                    //                 currentreviewedStepData.datepickerRework,
                                    //         })
                                    //     )
                                    // );
                                } else {
                                    setData(
                                        Array.from(data).map((element) => {
                                            if (
                                                element.sequence ===
                                                currentSequence
                                            ) {
                                                return {
                                                    ...element,
                                                    compareTo: {
                                                        ...element.compareTo,
                                                        coverLetterDetails:
                                                            element.compareTo &&
                                                            Object.keys(
                                                                element.compareTo
                                                            ).length > 0 &&
                                                            element.compareTo
                                                                .coverLetterDetails !==
                                                                null
                                                                ? {
                                                                      ...element
                                                                          .compareTo
                                                                          .coverLetterDetails,
                                                                      sortedVertices:
                                                                          Array.from(
                                                                              element
                                                                                  .compareTo
                                                                                  .coverLetterDetails
                                                                                  .sortedVertices ??
                                                                                  []
                                                                          ).map(
                                                                              (
                                                                                  o
                                                                              ) => ({
                                                                                  ...o,
                                                                                  isActive:
                                                                                      false,
                                                                                  isDrawn:
                                                                                      getDrawnStatus(
                                                                                          o.isDrawn,
                                                                                          o.isActive
                                                                                      ),
                                                                              })
                                                                          ),
                                                                  }
                                                                : null,
                                                        sortedVertices:
                                                            Array.from(
                                                                element
                                                                    .compareTo
                                                                    .sortedVertices ??
                                                                    []
                                                            ).map((o) => ({
                                                                ...o,
                                                                isActive: false,
                                                                isDrawn:
                                                                    getDrawnStatus(
                                                                        o.isDrawn,
                                                                        o.isActive
                                                                    ),
                                                            })),
                                                    },
                                                    compareWith: {
                                                        ...element.compareWith,
                                                        coverLetterDetails:
                                                            element.compareWith &&
                                                            Object.keys(
                                                                element.compareWith
                                                            ).length > 0 &&
                                                            element.compareWith
                                                                .coverLetterDetails !==
                                                                null
                                                                ? {
                                                                      ...element
                                                                          .compareWith
                                                                          .coverLetterDetails,
                                                                      sortedVertices:
                                                                          Array.from(
                                                                              element
                                                                                  .compareWith
                                                                                  .coverLetterDetails
                                                                                  .sortedVertices ??
                                                                                  []
                                                                          ).map(
                                                                              (
                                                                                  o
                                                                              ) => ({
                                                                                  ...o,
                                                                                  isActive:
                                                                                      false,
                                                                                  isDrawn:
                                                                                      getDrawnStatus(
                                                                                          o.isDrawn,
                                                                                          o.isActive
                                                                                      ),
                                                                              })
                                                                          ),
                                                                  }
                                                                : null,
                                                        sortedVertices:
                                                            Array.from(
                                                                element
                                                                    .compareWith
                                                                    .sortedVertices ??
                                                                    []
                                                            ).map((o) => ({
                                                                ...o,
                                                                isActive: false,
                                                                isDrawn:
                                                                    getDrawnStatus(
                                                                        o.isDrawn,
                                                                        o.isActive
                                                                    ),
                                                            })),
                                                    },
                                                    firstReason:
                                                        currentreviewedStepData.firstReason,
                                                    secondReason:
                                                        currentreviewedStepData.secondReason,
                                                    firstReasonPreDefined:
                                                        currentreviewedStepData.firstReasonPreDefined,
                                                    secondReasonPreDefined:
                                                        currentreviewedStepData.secondReasonPreDefined,

                                                    isFirstDocument:
                                                        currentreviewedStepData.isFirstDocument,
                                                    isSecondDocument:
                                                        currentreviewedStepData.isSecondDocument,
                                                    // expiryDate: values.datepickerRework,
                                                };
                                            }
                                            return element;
                                        })
                                    );
                                }
                            } else if (openDetail === 'Escalated') {
                                setData(
                                    Array.from(data).map((element) => {
                                        if (
                                            element.sequence === currentSequence
                                        ) {
                                            return {
                                                ...element,
                                                compareTo: {
                                                    ...element.compareTo,
                                                    coverLetterDetails:
                                                        element.compareTo &&
                                                        Object.keys(
                                                            element.compareTo
                                                        ).length > 0 &&
                                                        element.compareTo
                                                            .coverLetterDetails !==
                                                            null
                                                            ? {
                                                                  ...element
                                                                      .compareTo
                                                                      .coverLetterDetails,
                                                                  sortedVertices:
                                                                      Array.from(
                                                                          element
                                                                              .compareTo
                                                                              .coverLetterDetails
                                                                              .sortedVertices ??
                                                                              []
                                                                      ).map(
                                                                          (
                                                                              o
                                                                          ) => ({
                                                                              ...o,
                                                                              isActive:
                                                                                  false,
                                                                              isDrawn:
                                                                                  getDrawnStatus(
                                                                                      o.isDrawn,
                                                                                      o.isActive
                                                                                  ),
                                                                          })
                                                                      ),
                                                              }
                                                            : null,
                                                    sortedVertices: Array.from(
                                                        element.compareTo
                                                            .sortedVertices ??
                                                            []
                                                    ).map((o) => ({
                                                        ...o,
                                                        isActive: false,
                                                        isDrawn: getDrawnStatus(
                                                            o.isDrawn,
                                                            o.isActive
                                                        ),
                                                    })),
                                                },
                                                compareWith: {
                                                    ...element.compareWith,
                                                    coverLetterDetails:
                                                        element.compareWith &&
                                                        Object.keys(
                                                            element.compareWith
                                                        ).length > 0 &&
                                                        element.compareWith
                                                            .coverLetterDetails !==
                                                            null
                                                            ? {
                                                                  ...element
                                                                      .compareWith
                                                                      .coverLetterDetails,
                                                                  sortedVertices:
                                                                      Array.from(
                                                                          element
                                                                              .compareWith
                                                                              .coverLetterDetails
                                                                              .sortedVertices ??
                                                                              []
                                                                      ).map(
                                                                          (
                                                                              o
                                                                          ) => ({
                                                                              ...o,
                                                                              isActive:
                                                                                  false,
                                                                              isDrawn:
                                                                                  getDrawnStatus(
                                                                                      o.isDrawn,
                                                                                      o.isActive
                                                                                  ),
                                                                          })
                                                                      ),
                                                              }
                                                            : null,
                                                    sortedVertices: Array.from(
                                                        element.compareWith
                                                            .sortedVertices ??
                                                            []
                                                    ).map((o) => ({
                                                        ...o,
                                                        isActive: false,
                                                        isDrawn: getDrawnStatus(
                                                            o.isDrawn,
                                                            o.isActive
                                                        ),
                                                    })),
                                                },
                                                escalatedReason:
                                                    currentreviewedStepData.escalatedReason,
                                            };
                                        }
                                        return element;
                                    })
                                );
                            } else {
                                setData(
                                    Array.from(data).map((element) => {
                                        if (
                                            element.sequence === currentSequence
                                        ) {
                                            return {
                                                ...element,
                                                compareTo: {
                                                    ...element.compareTo,
                                                    coverLetterDetails:
                                                        element.compareTo &&
                                                        Object.keys(
                                                            element.compareTo
                                                        ).length > 0 &&
                                                        element.compareTo
                                                            .coverLetterDetails !==
                                                            null
                                                            ? {
                                                                  ...element
                                                                      .compareTo
                                                                      .coverLetterDetails,
                                                                  sortedVertices:
                                                                      Array.from(
                                                                          element
                                                                              .compareTo
                                                                              .coverLetterDetails
                                                                              .sortedVertices ??
                                                                              []
                                                                      ).map(
                                                                          (
                                                                              o
                                                                          ) => ({
                                                                              ...o,
                                                                              isActive:
                                                                                  false,
                                                                              isDrawn:
                                                                                  getDrawnStatus(
                                                                                      o.isDrawn,
                                                                                      o.isActive
                                                                                  ),
                                                                          })
                                                                      ),
                                                              }
                                                            : null,
                                                    sortedVertices: Array.from(
                                                        element.compareTo
                                                            .sortedVertices ??
                                                            []
                                                    ).map((o) => ({
                                                        ...o,
                                                        isActive: false,
                                                        isDrawn: getDrawnStatus(
                                                            o.isDrawn,
                                                            o.isActive
                                                        ),
                                                    })),
                                                },
                                                compareWith: {
                                                    ...element.compareWith,
                                                    coverLetterDetails:
                                                        element.compareWith &&
                                                        Object.keys(
                                                            element.compareWith
                                                        ).length > 0 &&
                                                        element.compareWith
                                                            .coverLetterDetails !==
                                                            null
                                                            ? {
                                                                  ...element
                                                                      .compareWith
                                                                      .coverLetterDetails,
                                                                  sortedVertices:
                                                                      Array.from(
                                                                          element
                                                                              .compareWith
                                                                              .coverLetterDetails
                                                                              .sortedVertices ??
                                                                              []
                                                                      ).map(
                                                                          (
                                                                              o
                                                                          ) => ({
                                                                              ...o,
                                                                              isActive:
                                                                                  false,
                                                                              isDrawn:
                                                                                  getDrawnStatus(
                                                                                      o.isDrawn,
                                                                                      o.isActive
                                                                                  ),
                                                                          })
                                                                      ),
                                                              }
                                                            : null,
                                                    sortedVertices: Array.from(
                                                        element.compareWith
                                                            .sortedVertices ??
                                                            []
                                                    ).map((o) => ({
                                                        ...o,
                                                        isActive: false,
                                                        isDrawn: getDrawnStatus(
                                                            o.isDrawn,
                                                            o.isActive
                                                        ),
                                                    })),
                                                },
                                            };
                                        }
                                        return element;
                                    })
                                );
                            }
                            setOpenDetail(stepData?.result);
                            setSequenceData(stepData);
                            setStep(stepData?.step ?? 0);
                            setSequence(jumpStep);
                            // handleReset();
                        }
                        const helper = getFieldHelpers('dropdownStep');
                        helper.setValue('', true);
                    }
                }
            },
            [
                SolicitorsData,
                currentSequence,
                data,
                getDrawnStatus,
                getFieldHelpers,
                onStepClicked,
                openDetail,
                setFieldTouched,
                validateForm,
                values.reviewedSteps,
            ]
        );
        return (
            <Box className="footer document-solicitor-footer">
                <Grid container spacing={2}>
                    <Grid item spacing={0} xs={12}>
                        <Box className="action-to-progress-document-solicitor">
                            <Grid
                                container
                                spacing={2}
                                className="container-document-solicitor"
                            >
                                <Grid
                                    item
                                    xs={3}
                                    className="document-solicitor-item"
                                >
                                    <Stack direction="row">
                                        <AppPrimaryButton
                                            title="Back"
                                            className={
                                                currentSequence > 1
                                                    ? 'btn-small back-button'
                                                    : 'btn-small back-button-normal'
                                            }
                                            startIcon={<ArrowBackIcon />}
                                            disabled={isActiveBackNavigation}
                                            onClick={
                                                actionOnProcessedToStepPrev
                                            }
                                        />
                                        {currentSequence > 1 && (
                                            <IconButton
                                                aria-label="close"
                                                className="container-close"
                                                disabled={
                                                    isActiveBackNavigation
                                                }
                                                onClick={handleCloseButton}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Grid>
                                <Grid
                                    item
                                    xs={6}
                                    className="document-solicitor-item"
                                >
                                    <Typography className="check-step">
                                        {`Check ${currentSequence} out of ${maxSequence}`}
                                    </Typography>
                                    <Typography
                                        variant="h4"
                                        className="check-context"
                                    >
                                        {`${
                                            currentSequenceData?.description ??
                                            ''
                                        }`}
                                    </Typography>
                                    {currentSequenceData &&
                                        currentSequenceData.displayMessageRequired &&
                                        currentSequenceData.displayMessage !==
                                            null &&
                                        currentSequenceData.displayMessage !==
                                            '' && (
                                            <Typography
                                                component="div"
                                                className="check-context"
                                            >
                                                {`${currentSequenceData?.displayMessage}`}
                                            </Typography>
                                        )}
                                </Grid>
                                <Grid
                                    item
                                    xs={3}
                                    className="document-solicitor-item"
                                >
                                    <Stack direction="row">
                                        <AppFormikSelect
                                            testid="dropdownStep"
                                            disableSaving
                                            name="dropdownStep"
                                            notched={false}
                                            labelId="dropdownStep-label"
                                            MenuProps={{
                                                className:
                                                    'menu-step-selection footer-document-viewer',
                                            }}
                                            className="dropdownstep"
                                            onChange={handleJumpToAnotherStep}
                                            placeholder="Checklist Steps"
                                            label="Checklist Steps"
                                            notEmphasized
                                            disabled={
                                                dropDownSequenceMenu.length ===
                                                0
                                            }
                                            items={dropDownSequenceMenu}
                                        />
                                        {/* {currentSequence > 1 && (
                                            <IconButton
                                                aria-label="close"
                                                className="container-close"
                                                disabled={
                                                    isActiveBackNavigation
                                                }
                                                onClick={handleCloseButton}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        )} */}
                                        <AppPrimaryButton
                                            disabled={
                                                (data.length === 0 ||
                                                    (isRightJSONUri === false &&
                                                        currentSequenceData
                                                            ?.compareTo.file &&
                                                        Array.isArray(
                                                            currentSequenceData
                                                                ?.compareTo
                                                                .sortedVertices
                                                        ) &&
                                                        currentSequenceData
                                                            ?.compareTo
                                                            .sortedVertices
                                                            .length === 0) ||
                                                    (isLeftJSONUri === false &&
                                                        currentSequenceData
                                                            ?.compareWith
                                                            .file &&
                                                        Array.isArray(
                                                            currentSequenceData
                                                                ?.compareWith
                                                                .sortedVertices
                                                        ) &&
                                                        currentSequenceData
                                                            ?.compareWith
                                                            .sortedVertices
                                                            .length === 0) ||
                                                    (isLeftJSONUri === false &&
                                                        currentSequenceData
                                                            ?.compareWith
                                                            .sortedVertices ===
                                                            null) ||
                                                    (isRightJSONUri === false &&
                                                        currentSequenceData
                                                            ?.compareTo
                                                            .sortedVertices ===
                                                            null) ||
                                                    (isRightJSONUri &&
                                                        isRightJSONPath ===
                                                            false) ||
                                                    (isLeftJSONUri &&
                                                        isLeftJSONPath ===
                                                            false)) &&
                                                currentSequenceData?.compareTo
                                                    .category !==
                                                    'cover letter' &&
                                                currentSequenceData?.compareWith
                                                    .category !==
                                                    'cover letter' &&
                                                openDetail !==
                                                    'Document Missing' &&
                                                openDetail !== 'N/A' &&
                                                openDetail !==
                                                    'Send For Rework' &&
                                                openDetail !== 'Escalated' &&
                                                openDetail !==
                                                    'Paper Document' &&
                                                openDetail !== 'Hand Signed' &&
                                                openDetail !==
                                                    'Sign Not Verified' &&
                                                isMissingDocument === false
                                            }
                                            title="Next"
                                            className="btn-small next-button-normal"
                                            endIcon={<ArrowForwardIcon />}
                                            onClick={
                                                actionOnProcessedToStepNext
                                            }
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                    <Grid item spacing={0} xs={12}>
                        <Box className="action-tab-selection-document-solicitor">
                            <ButtonGroup
                                className="button-tab-document-solicitor"
                                variant="contained"
                                aria-label="outlined primary button group"
                            >
                                <AppPrimaryButton
                                    testid="rework-button"
                                    title="Send to Rework"
                                    disabled={
                                        (isMissingDocument === true ||
                                            isReworkPosibleFlag === true) &&
                                        (role === 'operator' ||
                                            isEscalated === false)
                                            ? false
                                            : ((isPaperDocument ||
                                                  isEscalatedFlag) &&
                                                  role === 'operator') ||
                                              (isEscalated &&
                                                  role !== 'operator')
                                    }
                                    className={
                                        openDetail !== 'Send For Rework'
                                            ? 'btn-small'
                                            : 'btn-small selected'
                                    }
                                    onClick={() =>
                                        actionOnTaskDetailToChangeValidation(
                                            'Send For Rework',
                                            currentSequence
                                        )
                                    }
                                />
                                <AppPrimaryButton
                                    testid="not-applicable-button"
                                    title="Not Applicable"
                                    disabled={isPaperDocument}
                                    className={
                                        openDetail !== 'N/A'
                                            ? 'btn-small'
                                            : 'btn-small selected'
                                    }
                                    onClick={() =>
                                        actionOnTaskDetailToChangeValidation(
                                            'N/A',
                                            currentSequence
                                        )
                                    }
                                />
                                <AppPrimaryButton
                                    testid="document-missing-button"
                                    title="Document Missing"
                                    disabled={
                                        isPaperDocument ||
                                        isMissingDocument !== true
                                    }
                                    className={
                                        openDetail !== 'Document Missing'
                                            ? 'btn-small'
                                            : 'btn-small selected'
                                    }
                                    onClick={() =>
                                        actionOnTaskDetailToChangeValidation(
                                            'Document Missing',
                                            currentSequence
                                        )
                                    }
                                />
                                {/* <AppPrimaryButton
                                    testid="paper-document-button"
                                    title="Paper Document"
                                    className={
                                        openDetail !== 'Paper Document'
                                            ? 'btn-small'
                                            : 'btn-small selected'
                                    }
                                    disabled={!isPaperDocument}
                                /> */}

                                <AppPrimaryButton
                                    testid="escalated-button"
                                    title="Escalate"
                                    className={
                                        openDetail !== 'Escalated'
                                            ? 'btn-small'
                                            : 'btn-small selected'
                                    }
                                    disabled={disabledEscalateButton}
                                    onClick={() =>
                                        actionOnTaskDetailToChangeValidation(
                                            'Escalated',
                                            currentSequence
                                        )
                                    }
                                />
                                {/* <AppPrimaryButton
                                    testid="hand-signed-button"
                                    title="Hand signed"
                                    className={
                                        openDetail !== 'Hand Signed'
                                            ? 'btn-small'
                                            : 'btn-small selected'
                                    }
                                    disabled={
                                        isPaperDocument || isMissingDocument
                                    }
                                    onClick={() =>
                                        actionOnTaskDetailToChangeValidation(
                                            'Hand Signed',
                                            currentSequence
                                        )
                                    }
                                /> */}
                                {/* <AppPrimaryButton
                                    testid="hand-signed-button"
                                    title="Sign Not Verified"
                                    disabled={
                                        isPaperDocument || isMissingDocument
                                    }
                                    className={
                                        openDetail !== 'Sign Not Verified'
                                            ? 'btn-small'
                                            : 'btn-small selected'
                                    }
                                    onClick={() =>
                                        actionOnTaskDetailToChangeValidation(
                                            'Sign Not Verified',
                                            currentSequence
                                        )
                                    }
                                /> */}
                                <AppPrimaryButton
                                    testid="mark-button"
                                    title="Mark as correct"
                                    className={
                                        openDetail !== 'Correct'
                                            ? 'btn-small'
                                            : 'btn-small selected'
                                    }
                                    disabled={isPaperDocument}
                                    onClick={() =>
                                        actionOnTaskDetailToChangeValidation(
                                            'Correct',
                                            currentSequence
                                        )
                                    }
                                />
                            </ButtonGroup>
                        </Box>
                    </Grid>
                    <FieldArray
                        name="reviewedSteps"
                        render={() => (
                            <>
                                {values.reviewedSteps.map(
                                    (Steps: Steps, index: number) => {
                                        if (
                                            data.some(
                                                (o, i) =>
                                                    o.sequence ===
                                                        currentSequence &&
                                                    i === index
                                            )
                                        ) {
                                            return (
                                                <>
                                                    <AppFormikTextField
                                                        name={`reviewedSteps[${index}].isEscalated`}
                                                        inputProps={{
                                                            type: 'hidden',
                                                        }}
                                                        className="isEscalated"
                                                    />
                                                    <AppFormikTextField
                                                        name={`reviewedSteps[${index}].isRead`}
                                                        inputProps={{
                                                            type: 'hidden',
                                                        }}
                                                        className="isRead"
                                                    />
                                                    <AppFormikTextField
                                                        name={`reviewedSteps[${index}].isRework`}
                                                        inputProps={{
                                                            type: 'hidden',
                                                        }}
                                                        className="isRework"
                                                    />
                                                    <AppFormikTextField
                                                        name={`reviewedSteps[${index}].isDisplayedEscalatedModal`}
                                                        inputProps={{
                                                            type: 'hidden',
                                                        }}
                                                        className="isDisplayedEscalatedModal"
                                                    />
                                                    {openDetail ===
                                                        'Send For Rework' && (
                                                        <Grid
                                                            container
                                                            item
                                                            spacing={0}
                                                            xs={12}
                                                            className="container-document-solicitor-detail"
                                                        >
                                                            <Grid
                                                                item
                                                                xs={8}
                                                                className="categorise-selection solicitor-detail-item"
                                                            >
                                                                <Typography
                                                                    variant="h5"
                                                                    id={`mui-component-select-reviewedSteps[${index}].solicitor`}
                                                                >
                                                                    Select
                                                                    Loanwriter
                                                                </Typography>
                                                                <Box className="container-select">
                                                                    <AppFormikSelect
                                                                        testid="solicitor-dropdown"
                                                                        name={`reviewedSteps[${index}].solicitor`}
                                                                        notched={
                                                                            false
                                                                        }
                                                                        labelId="fix-selection-label"
                                                                        MenuProps={{
                                                                            className:
                                                                                'menu-fix-selection footer-document-viewer',
                                                                        }}
                                                                        label="Loanwriter"
                                                                        items={
                                                                            Solicitors
                                                                        }
                                                                    />
                                                                </Box>
                                                            </Grid>
                                                            <Grid
                                                                item
                                                                xs={4}
                                                                className="dateselection solicitor-detail-item"
                                                            >
                                                                <Typography variant="h5">
                                                                    Expiry date
                                                                </Typography>
                                                                <FormControl
                                                                    fullWidth
                                                                    className="container-dateselection"
                                                                >
                                                                    <AppFormikDatePicker
                                                                        testid="dateselector-dropdown"
                                                                        disablePast
                                                                        disableToday
                                                                        disabled
                                                                        name={`reviewedSteps[${index}].datepickerRework`}
                                                                        InputProps={{
                                                                            name: `reviewedSteps[${index}].datepickerRework`,
                                                                            'aria-labelledby':
                                                                                'rework-datepicker-label',
                                                                            notched:
                                                                                false,
                                                                        }}
                                                                        value={
                                                                            undefined
                                                                        }
                                                                        // onChange={() =>
                                                                        //      console.log(
                                                                        //          'date'
                                                                        //      )
                                                                        // }
                                                                        renderInput={(
                                                                            props: any
                                                                        ) => (
                                                                            <TextField
                                                                                {...props}
                                                                            />
                                                                        )}
                                                                    />
                                                                </FormControl>
                                                            </Grid>
                                                            {typeof currentSequenceData?.compareWith ===
                                                                'object' &&
                                                                Object.keys(
                                                                    currentSequenceData.compareWith
                                                                ).filter(
                                                                    (o) =>
                                                                        o !==
                                                                            'sortedVertices' &&
                                                                        o !==
                                                                            'coverLetterDetails'
                                                                ).length >
                                                                    0 && (
                                                                    <Grid
                                                                        container
                                                                        item
                                                                        className="send-solicitor-instruction  solicitor-detail-item"
                                                                        xs={12}
                                                                    >
                                                                        <FormControl id="reason-to-send-solicitor">
                                                                            <Grid
                                                                                container
                                                                                item
                                                                                xs={
                                                                                    12
                                                                                }
                                                                                spacing={
                                                                                    0
                                                                                }
                                                                                className="send-solicitor-container"
                                                                            >
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                    sm={
                                                                                        12
                                                                                    }
                                                                                    md={
                                                                                        4
                                                                                    }
                                                                                    lg={
                                                                                        3
                                                                                    }
                                                                                >
                                                                                    <Stack
                                                                                        direction="row"
                                                                                        display="flex"
                                                                                        alignItems="center"
                                                                                    >
                                                                                        <Box
                                                                                            marginLeft={
                                                                                                2
                                                                                            }
                                                                                        >
                                                                                            <FormLabel id="checkbox-for-solicitor-instruction">
                                                                                                Select
                                                                                                the
                                                                                                reason
                                                                                            </FormLabel>
                                                                                            <FormGroup>
                                                                                                <AppFormikTextField
                                                                                                    name={`reviewedSteps[${index}].isFirstDocument`}
                                                                                                    inputProps={{
                                                                                                        type: 'hidden',
                                                                                                    }}
                                                                                                    className="isFirstDocument"
                                                                                                />
                                                                                                <AppRadioBox
                                                                                                    checked={
                                                                                                        isChekedFirst
                                                                                                    }
                                                                                                    aria-labelledby="checkbox-for-solicitor-instruction"
                                                                                                    testid="first-document-checkbox"
                                                                                                    // disabled={
                                                                                                    //     currentSequenceData
                                                                                                    //         ?.compareWith
                                                                                                    //         .documentId ===
                                                                                                    //     null
                                                                                                    // }
                                                                                                    onChange={
                                                                                                        handleCheckFirstDocument
                                                                                                    }
                                                                                                    value="viewall"
                                                                                                />
                                                                                            </FormGroup>
                                                                                        </Box>
                                                                                        <Box>
                                                                                            {' '}
                                                                                            <Typography variant="h5">
                                                                                                Category:
                                                                                                <strong>
                                                                                                    {' '}
                                                                                                    {
                                                                                                        currentSequenceData
                                                                                                            ?.compareWith
                                                                                                            .category
                                                                                                    }
                                                                                                </strong>
                                                                                            </Typography>
                                                                                            <Typography variant="h6">
                                                                                                {
                                                                                                    currentSequenceData
                                                                                                        ?.compareWith
                                                                                                        .placeholder
                                                                                                }
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    </Stack>
                                                                                </Grid>
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                    md={
                                                                                        3
                                                                                    }
                                                                                    lg={
                                                                                        3
                                                                                    }
                                                                                    className="textarea-solicitor"
                                                                                >
                                                                                    <AppFormikSelect
                                                                                        testid="firstReasonPreDefined"
                                                                                        name={`reviewedSteps[${index}].firstReasonPreDefined`}
                                                                                        notched={
                                                                                            false
                                                                                        }
                                                                                        labelId="firstReasonPreDefined-label"
                                                                                        MenuProps={{
                                                                                            className:
                                                                                                'menu-fix-selection footer-document-viewer',
                                                                                        }}
                                                                                        placeholder="Select predefined reason"
                                                                                        label="Predefined reason"
                                                                                        items={[
                                                                                            'Document blurred',
                                                                                            'Document cropped',
                                                                                            'Data / Information',
                                                                                            'Document Wrong',
                                                                                            'Document corrupted',
                                                                                            'Document missing',
                                                                                            'Other',
                                                                                        ]}
                                                                                        onChange={(
                                                                                            event: any
                                                                                        ) => {
                                                                                            handleCheckFirstDocument(
                                                                                                event,
                                                                                                true
                                                                                            );

                                                                                            if (
                                                                                                currentSequence
                                                                                            ) {
                                                                                                const message =
                                                                                                    event
                                                                                                        .target
                                                                                                        .value;
                                                                                                const currentIndex =
                                                                                                    data.findIndex(
                                                                                                        (
                                                                                                            o
                                                                                                        ) =>
                                                                                                            o.sequence ===
                                                                                                            currentSequence
                                                                                                    );

                                                                                                if (
                                                                                                    message &&
                                                                                                    currentSequenceData.firstReasonPreDefined !==
                                                                                                        message
                                                                                                ) {
                                                                                                    data?.forEach(
                                                                                                        (
                                                                                                            e,
                                                                                                            index
                                                                                                        ) => {
                                                                                                            if (
                                                                                                                index !==
                                                                                                                    currentIndex &&
                                                                                                                e.sequence >
                                                                                                                    currentSequence &&
                                                                                                                ((typeof e
                                                                                                                    .compareWith
                                                                                                                    .documentId ===
                                                                                                                    'undefined' &&
                                                                                                                    e
                                                                                                                        .compareWith
                                                                                                                        .category ===
                                                                                                                        currentSequenceData
                                                                                                                            .compareWith
                                                                                                                            .category) ||
                                                                                                                    (typeof currentSequenceData
                                                                                                                        ?.compareWith
                                                                                                                        .documentId !==
                                                                                                                        'undefined' &&
                                                                                                                        e
                                                                                                                            .compareWith
                                                                                                                            .documentId ===
                                                                                                                            currentSequenceData
                                                                                                                                ?.compareWith
                                                                                                                                .documentId &&
                                                                                                                        ((currentSequenceData
                                                                                                                            ?.compareWith
                                                                                                                            .documentId ===
                                                                                                                            null &&
                                                                                                                            e
                                                                                                                                .compareWith
                                                                                                                                .category ===
                                                                                                                                currentSequenceData
                                                                                                                                    .compareWith
                                                                                                                                    .category) ||
                                                                                                                            currentSequenceData
                                                                                                                                ?.compareWith
                                                                                                                                .documentId !==
                                                                                                                                null)))
                                                                                                            ) {
                                                                                                                const helpers =
                                                                                                                    getFieldHelpers(
                                                                                                                        `reviewedSteps[${index}].firstReasonPreDefined`
                                                                                                                    );
                                                                                                                const fieldHelper =
                                                                                                                    getFieldHelpers(
                                                                                                                        `reviewedSteps[${index}].isFirstDocument`
                                                                                                                    );

                                                                                                                fieldHelper.setValue(
                                                                                                                    true,
                                                                                                                    false
                                                                                                                );
                                                                                                                helpers.setValue(
                                                                                                                    message,
                                                                                                                    false
                                                                                                                );
                                                                                                            }

                                                                                                            if (
                                                                                                                index !==
                                                                                                                    currentIndex &&
                                                                                                                e.sequence >
                                                                                                                    currentSequence &&
                                                                                                                ((typeof e
                                                                                                                    .compareTo
                                                                                                                    .documentId ===
                                                                                                                    'undefined' &&
                                                                                                                    e
                                                                                                                        .compareTo
                                                                                                                        .category ===
                                                                                                                        currentSequenceData
                                                                                                                            .compareWith
                                                                                                                            .category) ||
                                                                                                                    (typeof currentSequenceData
                                                                                                                        ?.compareWith
                                                                                                                        .documentId !==
                                                                                                                        'undefined' &&
                                                                                                                        e
                                                                                                                            .compareTo
                                                                                                                            .documentId ===
                                                                                                                            currentSequenceData
                                                                                                                                ?.compareWith
                                                                                                                                .documentId &&
                                                                                                                        ((currentSequenceData
                                                                                                                            .compareWith
                                                                                                                            .documentId ===
                                                                                                                            null &&
                                                                                                                            e
                                                                                                                                .compareTo
                                                                                                                                .category ===
                                                                                                                                currentSequenceData
                                                                                                                                    .compareWith
                                                                                                                                    .category) ||
                                                                                                                            currentSequenceData
                                                                                                                                .compareWith
                                                                                                                                .documentId !==
                                                                                                                                null)))
                                                                                                            ) {
                                                                                                                const helpers =
                                                                                                                    getFieldHelpers(
                                                                                                                        `reviewedSteps[${index}].secondReasonPreDefined`
                                                                                                                    );
                                                                                                                const fieldHelper =
                                                                                                                    getFieldHelpers(
                                                                                                                        `reviewedSteps[${index}].isSecondDocument`
                                                                                                                    );

                                                                                                                fieldHelper.setValue(
                                                                                                                    true,
                                                                                                                    false
                                                                                                                );
                                                                                                                helpers.setValue(
                                                                                                                    message,
                                                                                                                    false
                                                                                                                );
                                                                                                            }
                                                                                                        }
                                                                                                    );
                                                                                                    //    footer.setCheckSecondDocument(
                                                                                                    //        true,
                                                                                                    //        String(meta.value),
                                                                                                    //        rightCategory
                                                                                                    //    );
                                                                                                }
                                                                                            }

                                                                                            // setFieldValue(
                                                                                            //     `reviewedSteps[${index}].firstReason`,
                                                                                            //     event
                                                                                            //         .target
                                                                                            //         .value
                                                                                            // );
                                                                                        }}
                                                                                    />
                                                                                </Grid>
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                    md={
                                                                                        5
                                                                                    }
                                                                                    lg={
                                                                                        6
                                                                                    }
                                                                                    className="textarea-solicitor"
                                                                                >
                                                                                    <AppFormikTextArea
                                                                                        disabled={
                                                                                            !isChekedFirst
                                                                                        }
                                                                                        notched={
                                                                                            false
                                                                                        }
                                                                                        maxLength={
                                                                                            500
                                                                                        }
                                                                                        name={`reviewedSteps[${index}].firstReason`}
                                                                                        label="Write a message / Instructions"
                                                                                        // placeholder="Message for solicitor explaining what should be reworked"
                                                                                        rows={
                                                                                            1
                                                                                        }
                                                                                        testid="document-first-reason"
                                                                                        focused={
                                                                                            isChekedFirst
                                                                                        }
                                                                                        onChange={(
                                                                                            event: any
                                                                                        ) =>
                                                                                            DocumentMetaProps?.setFirstReasonRework(
                                                                                                event
                                                                                                    .target
                                                                                                    .value
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </Grid>
                                                                            </Grid>
                                                                        </FormControl>
                                                                    </Grid>
                                                                )}

                                                            {typeof currentSequenceData?.compareTo ===
                                                                'object' &&
                                                                Object.keys(
                                                                    currentSequenceData.compareTo
                                                                ).filter(
                                                                    (o) =>
                                                                        o !==
                                                                            'sortedVertices' &&
                                                                        o !==
                                                                            'coverLetterDetails'
                                                                ).length >
                                                                    0 && (
                                                                    <Grid
                                                                        container
                                                                        item
                                                                        className="send-solicitor-instruction solicitor-detail-item"
                                                                        xs={12}
                                                                    >
                                                                        <FormControl id="reason-to-send-solicitor">
                                                                            <Grid
                                                                                container
                                                                                item
                                                                                xs={
                                                                                    12
                                                                                }
                                                                                spacing={
                                                                                    0
                                                                                }
                                                                                className="send-solicitor-container"
                                                                            >
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                    sm={
                                                                                        12
                                                                                    }
                                                                                    md={
                                                                                        4
                                                                                    }
                                                                                    lg={
                                                                                        3
                                                                                    }
                                                                                >
                                                                                    <Stack
                                                                                        direction="row"
                                                                                        display="flex"
                                                                                        alignItems="center"
                                                                                    >
                                                                                        <Box
                                                                                            marginLeft={
                                                                                                2
                                                                                            }
                                                                                        >
                                                                                            <FormLabel id="checkbox-for-solicitor-instruction">
                                                                                                Select
                                                                                                the
                                                                                                reason
                                                                                            </FormLabel>
                                                                                            <FormGroup>
                                                                                                <AppFormikTextField
                                                                                                    name={`reviewedSteps[${index}].isSecondDocument`}
                                                                                                    inputProps={{
                                                                                                        type: 'hidden',
                                                                                                    }}
                                                                                                    className="isSecondDocument"
                                                                                                />
                                                                                                <AppRadioBox
                                                                                                    checked={
                                                                                                        isChekedSecond
                                                                                                    }
                                                                                                    aria-labelledby="checkbox-for-solicitor-instruction"
                                                                                                    testid="second-document-checkbox"
                                                                                                    // disabled={
                                                                                                    //     currentSequenceData
                                                                                                    //         ?.compareTo
                                                                                                    //         .documentId ===
                                                                                                    //     null
                                                                                                    // }
                                                                                                    value="viewall"
                                                                                                    onChange={
                                                                                                        handleCheckSecondDocument
                                                                                                    }
                                                                                                />
                                                                                            </FormGroup>
                                                                                        </Box>
                                                                                        <Box>
                                                                                            {' '}
                                                                                            <Typography variant="h5">
                                                                                                Category:
                                                                                                <strong>
                                                                                                    {
                                                                                                        currentSequenceData
                                                                                                            ?.compareTo
                                                                                                            .category
                                                                                                    }
                                                                                                </strong>
                                                                                            </Typography>
                                                                                            <Typography variant="h6">
                                                                                                {
                                                                                                    currentSequenceData
                                                                                                        ?.compareTo
                                                                                                        .placeholder
                                                                                                }
                                                                                            </Typography>
                                                                                        </Box>
                                                                                    </Stack>
                                                                                </Grid>
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                    md={
                                                                                        3
                                                                                    }
                                                                                    lg={
                                                                                        3
                                                                                    }
                                                                                    className="textarea-solicitor"
                                                                                >
                                                                                    <AppFormikSelect
                                                                                        testid="secondReasonPreDefined"
                                                                                        name={`reviewedSteps[${index}].secondReasonPreDefined`}
                                                                                        notched={
                                                                                            false
                                                                                        }
                                                                                        labelId="secondReasonPreDefined-label"
                                                                                        MenuProps={{
                                                                                            className:
                                                                                                'menu-fix-selection footer-document-viewer',
                                                                                        }}
                                                                                        placeholder="Select predefined reason"
                                                                                        label="Predefined reason"
                                                                                        items={[
                                                                                            'Document blurred',
                                                                                            'Document cropped',
                                                                                            'Data / Information',
                                                                                            'Document Wrong',
                                                                                            'Document corrupted',
                                                                                            'Document missing',
                                                                                            'Other',
                                                                                        ]}
                                                                                        onChange={(
                                                                                            event: any
                                                                                        ) => {
                                                                                            handleCheckSecondDocument(
                                                                                                event,
                                                                                                true
                                                                                            );
                                                                                            if (
                                                                                                currentSequence
                                                                                            ) {
                                                                                                const message =
                                                                                                    event
                                                                                                        .target
                                                                                                        .value;
                                                                                                const currentIndex =
                                                                                                    data.findIndex(
                                                                                                        (
                                                                                                            o
                                                                                                        ) =>
                                                                                                            o.sequence ===
                                                                                                            currentSequence
                                                                                                    );

                                                                                                if (
                                                                                                    message &&
                                                                                                    currentSequenceData.secondReasonPreDefined !==
                                                                                                        message
                                                                                                ) {
                                                                                                    data?.forEach(
                                                                                                        (
                                                                                                            e,
                                                                                                            index
                                                                                                        ) => {
                                                                                                            if (
                                                                                                                index !==
                                                                                                                    currentIndex &&
                                                                                                                e.sequence >
                                                                                                                    currentSequence &&
                                                                                                                ((typeof e
                                                                                                                    .compareWith
                                                                                                                    .documentId ===
                                                                                                                    'undefined' &&
                                                                                                                    e
                                                                                                                        .compareWith
                                                                                                                        .category ===
                                                                                                                        currentSequenceData
                                                                                                                            .compareTo
                                                                                                                            .category) ||
                                                                                                                    (typeof currentSequenceData
                                                                                                                        ?.compareTo
                                                                                                                        .documentId !==
                                                                                                                        'undefined' &&
                                                                                                                        e
                                                                                                                            .compareWith
                                                                                                                            .documentId ===
                                                                                                                            currentSequenceData
                                                                                                                                ?.compareTo
                                                                                                                                .documentId &&
                                                                                                                        ((currentSequenceData
                                                                                                                            ?.compareTo
                                                                                                                            .documentId ==
                                                                                                                            null &&
                                                                                                                            e
                                                                                                                                .compareWith
                                                                                                                                .category ===
                                                                                                                                currentSequenceData
                                                                                                                                    .compareTo
                                                                                                                                    .category) ||
                                                                                                                            currentSequenceData
                                                                                                                                ?.compareTo
                                                                                                                                .documentId !==
                                                                                                                                null)))
                                                                                                            ) {
                                                                                                                const helpers =
                                                                                                                    getFieldHelpers(
                                                                                                                        `reviewedSteps[${index}].firstReasonPreDefined`
                                                                                                                    );
                                                                                                                const fieldHelper =
                                                                                                                    getFieldHelpers(
                                                                                                                        `reviewedSteps[${index}].isFirstDocument`
                                                                                                                    );

                                                                                                                fieldHelper.setValue(
                                                                                                                    true,
                                                                                                                    false
                                                                                                                );
                                                                                                                helpers.setValue(
                                                                                                                    message,
                                                                                                                    false
                                                                                                                );
                                                                                                            }
                                                                                                            if (
                                                                                                                index !==
                                                                                                                    currentIndex &&
                                                                                                                e.sequence >
                                                                                                                    currentSequence &&
                                                                                                                ((typeof e
                                                                                                                    .compareTo
                                                                                                                    .documentId ===
                                                                                                                    'undefined' &&
                                                                                                                    e
                                                                                                                        .compareTo
                                                                                                                        .category ===
                                                                                                                        currentSequenceData
                                                                                                                            .compareTo
                                                                                                                            .category) ||
                                                                                                                    (typeof currentSequenceData
                                                                                                                        ?.compareTo
                                                                                                                        .documentId !==
                                                                                                                        'undefined' &&
                                                                                                                        e
                                                                                                                            .compareTo
                                                                                                                            .documentId ===
                                                                                                                            currentSequenceData
                                                                                                                                ?.compareTo
                                                                                                                                .documentId &&
                                                                                                                        ((currentSequenceData
                                                                                                                            ?.compareTo
                                                                                                                            .documentId ===
                                                                                                                            null &&
                                                                                                                            e
                                                                                                                                .compareTo
                                                                                                                                .category ===
                                                                                                                                currentSequenceData
                                                                                                                                    .compareTo
                                                                                                                                    .category) ||
                                                                                                                            currentSequenceData
                                                                                                                                ?.compareTo
                                                                                                                                .documentId !==
                                                                                                                                null)))
                                                                                                            ) {
                                                                                                                const helpers =
                                                                                                                    getFieldHelpers(
                                                                                                                        `reviewedSteps[${index}].secondReasonPreDefined`
                                                                                                                    );
                                                                                                                const fieldHelper =
                                                                                                                    getFieldHelpers(
                                                                                                                        `reviewedSteps[${index}].isSecondDocument`
                                                                                                                    );

                                                                                                                fieldHelper.setValue(
                                                                                                                    true,
                                                                                                                    false
                                                                                                                );
                                                                                                                helpers.setValue(
                                                                                                                    message,
                                                                                                                    false
                                                                                                                );
                                                                                                            }
                                                                                                        }
                                                                                                    );
                                                                                                    //    footer.setCheckSecondDocument(
                                                                                                    //        true,
                                                                                                    //        String(meta.value),
                                                                                                    //        rightCategory
                                                                                                    //    );
                                                                                                }
                                                                                            }

                                                                                            // setFieldValue(
                                                                                            //     `reviewedSteps[${index}].secondReason`,
                                                                                            //     event
                                                                                            //         .target
                                                                                            //         .value
                                                                                            // );
                                                                                        }}
                                                                                    />
                                                                                </Grid>
                                                                                <Grid
                                                                                    item
                                                                                    xs={
                                                                                        12
                                                                                    }
                                                                                    md={
                                                                                        5
                                                                                    }
                                                                                    lg={
                                                                                        6
                                                                                    }
                                                                                    className="textarea-solicitor"
                                                                                >
                                                                                    <AppFormikTextArea
                                                                                        disabled={
                                                                                            !isChekedSecond
                                                                                        }
                                                                                        notched={
                                                                                            false
                                                                                        }
                                                                                        maxLength={
                                                                                            500
                                                                                        }
                                                                                        testid="document-second-reason"
                                                                                        name={`reviewedSteps[${index}].secondReason`}
                                                                                        label="Write a message / Instructions"
                                                                                        // placeholder="Message for solicitor explaining what should be reworked"
                                                                                        rows={
                                                                                            1
                                                                                        }
                                                                                        focused={
                                                                                            isChekedSecond
                                                                                        }
                                                                                        onChange={(
                                                                                            event: any
                                                                                        ) =>
                                                                                            DocumentMetaProps?.setSecondReasonRework(
                                                                                                event
                                                                                                    .target
                                                                                                    .value
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </Grid>
                                                                            </Grid>
                                                                        </FormControl>
                                                                    </Grid>
                                                                )}
                                                        </Grid>
                                                    )}
                                                    {openDetail ===
                                                        'Escalated' && (
                                                        <Grid
                                                            item
                                                            spacing={0}
                                                            xs={12}
                                                        >
                                                            <Box className="escalated-solicitor-message">
                                                                <InputLabel id="escalated-textarea-solicitor">
                                                                    Write a
                                                                    message /
                                                                    Instructions
                                                                </InputLabel>

                                                                <AppFormikTextArea
                                                                    testid="escalated-reason"
                                                                    name={`reviewedSteps[${index}].escalatedReason`}
                                                                    notched={
                                                                        false
                                                                    }
                                                                    maxLength={
                                                                        256
                                                                    }
                                                                    arialabelledby="escalated-textarea-solicitor"
                                                                    placeholder="Write a message / Instructions"
                                                                    rows={1}
                                                                />
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                </>
                                            );
                                        }
                                        return null;
                                    }
                                )}
                            </>
                        )}
                    />
                </Grid>
                <EscalatedModal ref={escalatedModalRef} />
            </Box>
        );
    }
);

export default Footer;
